import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Briefcase, Clock, FileText, Calendar as CalendarIcon,
  Users, ReceiptText, ClipboardList, StickyNote, Download, Trash2,
  File, FileImage, FileSpreadsheet, CheckCircle2, AlertCircle,
  User, Phone, Mail, Globe, MapPin, Plus, Scale, Banknote, ArrowDownCircle, X,
  Pencil, Circle, Flag, Share2, Copy, Check, MessageCircle, Receipt,
  Archive, FolderOpen, AlertTriangle, Hash, MessageSquare, Lock, Unlock,
} from 'lucide-react';
import { Card, CardBody, Badge, Button, EmptyState, Modal, Label, Input, Select, Textarea, StatusDropdown, ConfirmDeleteModal, HelpTooltip } from '../components/ui';
import { StartTimerButton } from '../components/FloatingTimer';
import { getMatter, updateMatter, closeMatter, archiveMatter, reopenMatter, setMatterConfidential, listDeadlines, createDeadline, updateDeadline, deleteDeadline, listTimeEntries, createTimeEntry, deleteTimeEntry, listTasks, createTask, updateTask, deleteTask, listDocuments, getDocumentUrl, updateDocument, deleteDocument, deleteDocumentFile, uploadDocumentFile, createDocument, listInvoices, createInvoice, createInvoiceLineItems, deleteInvoice, fetchContact, listContacts, listMatterNotes, createMatterNote, updateMatterNote, deleteMatterNote, listMatterUsers, addMatterUser, removeMatterUser, listFirmUsers, listMatterTeam, addMatterTeamMember, removeMatterTeamMember, listEmployees, listMatterPayments, listCaseCharges, updateMatterBilling, recordInstallmentPayment, addCaseCharge, updateCaseCharge, markCaseChargePaid, updateInvoice, getInvoice, deletePayment, deleteCaseCharge, invitePortalClient, listChannelsForMatter, createFirmChannel, setChannelMatter, createPortalDocumentRequest, cancelPortalDocumentRequest, acceptPortalDocumentRequest, rejectPortalDocumentRequest, listPortalDocumentRequestsForFirm, mapStorageLimitError } from '../lib/db';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { useRoute } from '../lib/router';
import { formatDate, formatDuration, formatMoney, fullName, classNames } from '../lib/formatters';
import { nextInvoiceNumber } from '../lib/invoiceNumber';
import { printInvoice } from '../lib/printInvoice';
import { printReceipt, nextReceiptNumber } from '../lib/printReceipt';
import GhanaTaxPicker from '../components/GhanaTaxPicker';
import { MATTER_STATUS, MATTER_PRIORITY, PRACTICE_AREAS, FEE_ARRANGEMENTS, DEADLINE_RULE_TEMPLATES, GHANA_TAX_LEVIES, CLOSING_REASONS, GHANA_JURISDICTIONS } from '../lib/constants';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

const TABS = [
  { key: 'overview', label: 'Overview', icon: Briefcase },
  { key: 'timeline', label: 'Timeline', icon: CalendarIcon },
  { key: 'deadlines', label: 'Deadlines', icon: CalendarIcon },
  { key: 'time', label: 'Time', icon: Clock },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'tasks', label: 'Tasks', icon: ClipboardList },
  { key: 'parties', label: 'Parties', icon: Users },
  { key: 'billing', label: 'Billing', icon: ReceiptText },
  { key: 'notes',   label: 'Notes',   icon: StickyNote },
];

export default function MatterDetailPage({ matterId }) {
  const { firmId, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const [, navigate] = useRoute();
  const log = useActivityLogger();
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [deadlines, setDeadlines] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [opposingCounsel, setOpposingCounsel] = useState(null);
  const [matterNotes,     setMatterNotes]     = useState([]);
  const [matterUsers,     setMatterUsers]     = useState([]);   // login users on this matter
  const [matterTeam,      setMatterTeam]      = useState([]);   // employees on this matter
  const [linkedChannels,  setLinkedChannels]  = useState([]);   // firm_channels tied to this matter
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [firmUsers,       setFirmUsers]       = useState([]);   // all login accounts
  const [employees,       setEmployees]       = useState([]);   // all non-login staff
  const [showManageTeam,  setShowManageTeam]  = useState(false);

  // Edit matter modal
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [saving, setSaving] = useState(false);

  // Close / Archive modal
  const [showClose, setShowClose] = useState(false);
  const [closeForm, setCloseForm] = useState({ close_date: new Date().toISOString().slice(0,10), reason_code: '', closing_reason: '', dismiss_open_items: true });
  const [closing, setClosing] = useState(false);

  // Reopen modal
  const [showReopen, setShowReopen] = useState(false);
  const [reopenCode, setReopenCode] = useState('');
  const [reopenOther, setReopenOther] = useState('');
  const [reopening, setReopening] = useState(false);

  // Log time modal
  const [showLogTime, setShowLogTime] = useState(false);
  const [timeForm, setTimeForm] = useState({ description: '', hours: '', minutes: '', billable: true, entry_date: new Date().toISOString().slice(0, 10), hourly_rate: '' });
  // eslint-disable-next-line no-unused-vars
  const [_loggingTime, _setLoggingTime] = useState(false);


  useEffect(() => {
    if (!firmId || !matterId) return;
    (async () => {
        try {
      setLoading(true);
      const { data } = await getMatter(matterId);
      setMatter(data);
      const [dl, te, tk, docs, inv, notes, mu, mt, fu, emp, ch] = await Promise.all([
        listDeadlines(firmId, false),
        listTimeEntries(firmId, { matterId }),
        listTasks(firmId, { matterId }),
        listDocuments(firmId, { matterId }),
        listInvoices(firmId, { matterId }),
        listMatterNotes(matterId),
        listMatterUsers(matterId),
        listMatterTeam(matterId),
        listFirmUsers(firmId),
        listEmployees(firmId),
        listChannelsForMatter(matterId),
      ]);
      setDeadlines((dl.data || []).filter((d) => d.matter_id === matterId));
      setTimeEntries(te.data || []);
      setTasks(tk.data || []);
      setDocuments(docs.data || []);
      setInvoices((inv.data || []).filter((i) => i.matter_id === matterId));
      setMatterNotes(notes.data || []);
      setMatterUsers(mu.data || []);
      setMatterTeam(mt.data || []);
      setFirmUsers(fu.data || []);
      setEmployees(emp.data || []);
      setLinkedChannels(ch.data || []);
      if (data?.opposing_counsel_id) {
        const { data: oc } = await fetchContact(data.opposing_counsel_id);
        setOpposingCounsel(oc);
      }
        } finally {
          setLoading(false);
        }
      })();
  }, [firmId, matterId, refreshSignal]);

  const totals = useMemo(() => {
    const billable = timeEntries.filter((t) => t.billable);
    const mins = billable.reduce((s, t) => s + (t.duration_minutes || 0), 0);
    const fees = billable.reduce((s, t) => s + Number(t.amount || 0), 0);
    return { mins, fees };
  }, [timeEntries]);

  const changeStatus = async (newStatus) => {
    const prev = matter;
    setMatter((m) => ({ ...m, status: newStatus }));
    const { error } = await updateMatter(matterId, { status: newStatus });
    if (error) { toast.error(error.message); setMatter(prev); return; }
    log('matter', matterId, 'status_changed', `Status → ${newStatus}`, matterId);
  };

  const openEdit = () => {
    setEditForm({
      title: matter.title || '',
      description: matter.description || '',
      status: matter.status || 'open',
      priority: matter.priority || 'medium',
      practice_area: matter.practice_area || '',
      jurisdiction: matter.jurisdiction || '',
      court: matter.court || '',
      court_case_number: matter.court_case_number || '',
      judge: matter.judge || '',
      statute_of_limitations: matter.statute_of_limitations || '',
      fee_arrangement: matter.fee_arrangement || 'hourly',
      budget_amount: matter.budget_amount || '',
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    const payload = {
      ...editForm,
      budget_amount: editForm.budget_amount ? Number(editForm.budget_amount) : null,
      statute_of_limitations: editForm.statute_of_limitations || null,
    };
    // Optimistic update — close immediately, save in background
    setMatter((prev) => ({ ...prev, ...payload }));
    setShowEdit(false);
    const { error } = await updateMatter(matterId, payload);
    if (error) {
      toast.error('Save failed: ' + error.message);
      // Revert on failure
      setMatter((prev) => ({ ...prev, ...matter }));
      return;
    }
    log('matter', matterId, 'updated', 'Case details updated', matterId);
  };

  const saveTime = async () => {
    const totalMins = (parseInt(timeForm.hours || 0) * 60) + parseInt(timeForm.minutes || 0);
    if (!totalMins) { toast.warning('Please enter a duration.'); return; }
    const rate = parseFloat(timeForm.hourly_rate || userProfile?.hourly_rate || 0);
    // eslint-disable-next-line no-unused-vars
    const _amount = (totalMins / 60) * rate;
    const payload = {
      matter_id: matterId,
      user_id: userProfile?.id,
      description: timeForm.description,
      entry_date: timeForm.entry_date,
      duration_minutes: totalMins,
      billable: timeForm.billable,
      hourly_rate: rate,
    };
    // Optimistic update — close immediately with a temp entry
    const tempEntry = { id: `temp-${Date.now()}`, ...payload };
    setTimeEntries((prev) => [tempEntry, ...prev]);
    setTimeForm({ description: '', hours: '', minutes: '', billable: true, entry_date: new Date().toISOString().slice(0, 10), hourly_rate: '' });
    setShowLogTime(false);
    const { data, error } = await createTimeEntry(firmId, payload);
    if (error) {
      toast.error('Failed to save time entry: ' + error.message);
      setTimeEntries((prev) => prev.filter((e) => e.id !== tempEntry.id));
    } else {
      // Replace temp entry with real one from DB
      setTimeEntries((prev) => prev.map((e) => e.id === tempEntry.id ? data : e));
      log('matter', data.id, 'created', 'Time entry added', matterId);
    }
  };

  // ── Close / Archive / Reopen handlers ──────────────────────────────────
  const handleClose = async () => {
    if (!closeForm.reason_code) { toast.warning('Please select a closing reason.'); return; }
    if (closeForm.reason_code === 'other' && !closeForm.closing_reason.trim()) { toast.warning('Please describe the reason for closing.'); return; }
    const finalReason = closeForm.reason_code === 'other'
      ? closeForm.closing_reason.trim()
      : CLOSING_REASONS.find((r) => r.value === closeForm.reason_code)?.label + (closeForm.closing_reason.trim() ? ` — ${closeForm.closing_reason.trim()}` : '');
    setClosing(true);
    const { data, error } = await closeMatter(matterId, {
      closing_reason: finalReason,
      close_date: closeForm.close_date,
    });
    if (error) { toast.error(error.message); setClosing(false); return; }

    // Optionally dismiss all open deadlines and tasks
    if (closeForm.dismiss_open_items) {
      const openDeadlines = deadlines.filter((d) => d.status === 'open');
      const openTasks     = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled');
      await Promise.all([
        ...openDeadlines.map((d) => updateDeadline(d.id, { status: 'dismissed' })),
        ...openTasks.map((t)     => updateTask(t.id,     { status: 'cancelled' })),
      ]);
      setDeadlines((prev) => prev.map((d) => d.status === 'open' ? { ...d, status: 'dismissed' } : d));
      setTasks((prev) =>     prev.map((t) => t.status !== 'done' && t.status !== 'cancelled' ? { ...t, status: 'cancelled' } : t));
    }

    setMatter((m) => ({ ...m, ...data }));
    setShowClose(false);
    setClosing(false);
    log('matter', matterId, 'closed', `Case closed — ${closeForm.closing_reason || 'no reason given'}`, matterId);
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this matter? It will be hidden from all normal lists and become read-only.')) return;
    const { data, error } = await archiveMatter(matterId);
    if (error) { toast.error(error.message); return; }
    setMatter((m) => ({ ...m, ...data }));
    log('matter', matterId, 'archived', 'Case archived', matterId);
  };

  // Flip the matter's confidential flag. When ON the matter and all its
  // artifacts (documents, tasks, time entries, notes, deadlines,
  // invoices, case charges) become invisible to firm members who aren't
  // Admin / Partner, the responsible attorney, or explicitly assigned
  // via matter_users / matter_team.
  //
  // The RPC auto-adds the caller to matter_users when flipping ON, so
  // they can't accidentally lock themselves out of their own matter.
  const toggleConfidential = async () => {
    const next = !matter.confidential;
    if (next && !window.confirm(
      'Mark this matter as Confidential?\n\n' +
      'Only Admin/Partner, the responsible attorney, and assigned team ' +
      'members will be able to see this matter and its documents, tasks, ' +
      'time entries, notes, deadlines, invoices, and case charges.\n\n' +
      'You can unlock it again at any time.'
    )) return;
    if (!next && !window.confirm(
      'Unlock this matter? It will become visible to all firm members again.'
    )) return;
    const { data: newFlag, error } = await setMatterConfidential(matterId, next);
    if (error) { toast.error(error.message || 'Failed to update confidentiality'); return; }
    setMatter((m) => ({ ...m, confidential: !!newFlag }));
    toast.success(newFlag ? 'Matter marked Confidential.' : 'Matter unlocked.');
    log('matter', matterId, newFlag ? 'confidential_on' : 'confidential_off',
        newFlag ? 'Marked Confidential' : 'Unlocked from Confidential',
        matterId);
  };

  const handleReopen = async () => {
    if (!reopenCode) { toast.warning('Please select a reason for reopening.'); return; }
    if (reopenCode === 'Other' && !reopenOther.trim()) { toast.warning('Please describe the reason for reopening.'); return; }
    const finalReason = reopenCode === 'Other' ? reopenOther.trim() : reopenCode;
    setReopening(true);
    const { data, error } = await reopenMatter(matterId);
    setReopening(false);
    if (error) { toast.error(error.message); return; }
    setMatter((m) => ({ ...m, ...data }));
    setShowReopen(false);
    setReopenCode(''); setReopenOther('');
    log('matter', matterId, 'reopened', `Case reopened — ${finalReason}`, matterId);
  };

  if (loading) return <div className="text-center text-sm text-slate-500 py-12">Loading matter…</div>;
  if (!matter) return (
    <EmptyState
      icon={Briefcase}
      title="Matter not found"
      description="It may have been archived or the link is invalid."
      action={<Button onClick={() => navigate('/matters')}>Back to case files</Button>}
    />
  );

  // eslint-disable-next-line no-unused-vars
  const _status = MATTER_STATUS.find((s) => s.value === matter.status);
  const priority = MATTER_PRIORITY.find((p) => p.value === matter.priority);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/matters')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 mb-3">
          <ArrowLeft size={14} /> All case files
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <StatusDropdown
                value={matter.status}
                options={MATTER_STATUS}
                onChange={changeStatus}
              />
              <Badge tone={priority?.tone}>{priority?.label} priority</Badge>
              {matter.fee_arrangement && <Badge tone="indigo">{matter.fee_arrangement.replace('_', ' ')}</Badge>}
              {matter.confidential && (
                <Badge tone="amber" title="Confidential: only Admin/Partner, the responsible attorney, and assigned team members can see this matter and its documents, tasks, time entries, notes, deadlines, invoices, and case charges.">
                  <Lock size={11} className="inline mr-1" />
                  Confidential
                </Badge>
              )}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{matter.title}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {matter.matter_number} · {fullName(matter.client) || 'No client'}
              {matter.practice_area && ` · ${matter.practice_area}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {matter.status === 'archived' ? (
              <Button variant="secondary" icon={FolderOpen} onClick={() => { setReopenCode(''); setReopenOther(''); setShowReopen(true); }}>Reopen</Button>
            ) : matter.status === 'closed' ? (
              <>
                <Button variant="secondary" icon={Archive} onClick={handleArchive}>Archive</Button>
                <Button variant="secondary" icon={FolderOpen} onClick={() => { setReopenCode(''); setReopenOther(''); setShowReopen(true); }}>Reopen</Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={openEdit}>Edit</Button>
                <Button
                  variant="secondary"
                  icon={matter.confidential ? Unlock : Lock}
                  onClick={toggleConfidential}
                  title={matter.confidential
                    ? 'Unlock this matter — make it visible to all firm members again'
                    : 'Mark as Confidential — restrict to Admin / Partner / assigned team only'}
                >
                  {matter.confidential ? 'Unlock' : 'Confidential'}
                </Button>
                <StartTimerButton matterId={matter.id} description={matter.title || ''} />
                <Button icon={Clock} onClick={() => setShowLogTime(true)}>Log time</Button>
                <Button variant="danger" icon={CheckCircle2} onClick={() => { setCloseForm({ close_date: new Date().toISOString().slice(0,10), reason_code: '', closing_reason: '', dismiss_open_items: true }); setShowClose(true); }}>Close matter</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Closed / Archived banner */}
      {matter.status === 'closed' && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
          <CheckCircle2 size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              Matter closed {matter.closed_at ? `on ${formatDate(matter.closed_at)}` : ''}
            </p>
            {matter.closing_reason && (
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">{matter.closing_reason}</p>
            )}
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
              This matter is closed. You can still view all records. Archive it to remove it from normal lists, or reopen it to resume work.
            </p>
          </div>
        </div>
      )}
      {matter.status === 'archived' && (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-4 py-3">
          <Archive size={18} className="text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Matter archived {matter.archived_at ? `on ${formatDate(matter.archived_at)}` : ''}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              This matter is archived and read-only. Reopen it if you need to resume work.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800 -mb-px overflow-x-auto">
        <div className="flex gap-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={classNames(
                  'inline-flex items-center gap-2 px-1 py-3 text-sm border-b-2 transition whitespace-nowrap',
                  active
                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200',
                )}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardBody>
              <h3 className="text-sm font-semibold mb-3">Description</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{matter.description || '—'}</p>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <Detail label="Jurisdiction" value={matter.jurisdiction} />
                <Detail label="Court" value={matter.court} />
                <Detail label="Court case #" value={matter.court_case_number} />
                <Detail label="Judge" value={matter.judge} />
                <Detail label="Opened" value={formatDate(matter.open_date)} />
                <Detail label="SOL" value={formatDate(matter.statute_of_limitations)} />
                <Detail label="Fee" value={matter.fee_arrangement} />
                <Detail label="Budget" value={matter.budget_amount ? formatMoney(matter.budget_amount) : '—'} />
              </dl>
            </CardBody>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardBody>
                <h3 className="text-sm font-semibold mb-3">At a glance</h3>
                <div className="space-y-2 text-sm">
                  <Row label="Billable (all-time)" value={formatDuration(totals.mins)} />
                  <Row label="Fees earned" value={formatMoney(totals.fees)} />
                  <Row label="Open deadlines" value={deadlines.filter((d) => d.status === 'open').length} />
                  <Row label="Open tasks" value={tasks.filter((t) => t.status === 'pending' || t.status === 'in_progress').length} />
                </div>
              </CardBody>
            </Card>

            {/* ── Team channels card ── */}
            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold flex items-center gap-1.5">
                    <MessageSquare size={13} className="text-indigo-500" />
                    Team channels
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={Plus}
                    disabled={creatingChannel}
                    onClick={async () => {
                      const baseName = (matter.matter_number || matter.title || 'matter').toLowerCase()
                        .replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'matter';
                      const channelName = `case-${baseName}`;
                      setCreatingChannel(true);
                      const { data: ch, error: cErr } = await createFirmChannel(
                        firmId, channelName, `Discussion for ${matter.title}`, userProfile?.id,
                      );
                      if (cErr) {
                        setCreatingChannel(false);
                        toast.error(cErr.message?.includes('duplicate')
                          ? `A channel named #${channelName} already exists. Link it manually from the channel header.`
                          : `Could not create channel: ${cErr.message}`);
                        return;
                      }
                      await setChannelMatter(ch.id, matterId);
                      setCreatingChannel(false);
                      setLinkedChannels((prev) => [...prev, { ...ch, matter_id: matterId }]);
                      toast.success(`Created #${channelName} and linked to this matter.`);
                    }}
                  >
                    New
                  </Button>
                </div>
                {linkedChannels.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    No channels linked. Create a channel for this matter, or link an existing channel from its header.
                  </p>
                ) : (
                  <ul className="space-y-1.5">
                    {linkedChannels.map((c) => (
                      <li key={c.id}>
                        <button
                          onClick={() => { window.location.hash = `#/messages?channel=${c.id}`; }}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                        >
                          {c.is_private
                            ? <User size={12} className="text-slate-400 flex-shrink-0" />
                            : <Hash size={12} className="text-slate-400 flex-shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                              {c.is_private ? c.name : `#${c.name}`}
                            </p>
                            {c.description && (
                              <p className="text-xs text-slate-500 truncate">{c.description}</p>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            {/* ── Case Team card ── */}
            <Card>
              <CardBody>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold">Case team</h3>
                  <Button size="sm" variant="ghost" icon={User} onClick={() => setShowManageTeam(true)}>
                    Manage
                  </Button>
                </div>
                {matterUsers.length === 0 && matterTeam.length === 0 ? (
                  <p className="text-xs text-slate-400">No team members assigned yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {/* Login users (attorneys / staff with accounts) */}
                    {matterUsers.map((mu) => (
                      <li key={`u-${mu.id}`} className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                          {(mu.user?.first_name?.[0] || '') + (mu.user?.last_name?.[0] || '')}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{fullName(mu.user) || mu.user?.email}</p>
                          <p className="text-xs text-slate-500">{mu.role_on_matter === 'lead_attorney' ? '⭐ Lead attorney' : mu.user?.role || 'Team member'}</p>
                        </div>
                      </li>
                    ))}
                    {/* Employees (non-login staff) */}
                    {matterTeam.map((mt) => (
                      <li key={`e-${mt.id}`} className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                          {(mt.employee?.first_name?.[0] || '') + (mt.employee?.last_name?.[0] || '')}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{fullName(mt.employee)}</p>
                          <p className="text-xs text-slate-500">{mt.employee?.role || mt.employee?.title || 'Staff'}</p>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex-shrink-0">Staff</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {tab === 'timeline'   && <Timeline timeEntries={timeEntries} deadlines={deadlines} tasks={tasks} documents={documents} />}
      {tab === 'deadlines'  && <DeadlinesTab deadlines={deadlines} setDeadlines={setDeadlines} firmId={firmId} matterId={matterId} />}
      {tab === 'time'       && <TimeEntriesList entries={timeEntries} setEntries={setTimeEntries} onLogTime={() => setShowLogTime(true)} firmId={firmId} />}
      {tab === 'tasks'      && <TasksTab tasks={tasks} setTasks={setTasks} firmId={firmId} matterId={matterId} />}
      {tab === 'documents'  && <DocumentsList documents={documents} setDocuments={setDocuments} firmId={firmId} matterId={matterId} matter={matter} />}
      {tab === 'parties'    && <PartiesTab matter={matter} setMatter={setMatter} opposingCounsel={opposingCounsel} setOpposingCounsel={setOpposingCounsel} firmId={firmId} matterId={matterId} />}
      {tab === 'billing'    && <BillingTab invoices={invoices} setInvoices={setInvoices} matter={matter} firmId={firmId} />}
      {tab === 'notes'      && <NotesTab notes={matterNotes} setNotes={setMatterNotes} firmId={firmId} matterId={matterId} userId={userProfile?.id} />}
      {!['overview','timeline','deadlines','time','tasks','documents','parties','billing','notes'].includes(tab) && (
        <Card><CardBody><EmptyState title={`${TABS.find((t) => t.key === tab)?.label} — coming soon`} /></CardBody></Card>
      )}

      {/* ── Edit matter modal ── */}
      <Modal
        open={showEdit}
        onClose={() => setShowEdit(false)}
        title="Edit matter"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={saveEdit}>Save changes</Button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Title</Label>
            <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          </div>
          <div>
            <Label>Status</Label>
            <Select options={MATTER_STATUS.map((s) => ({ value: s.value, label: s.label }))} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} />
          </div>
          <div>
            <Label>Priority</Label>
            <Select options={MATTER_PRIORITY.map((p) => ({ value: p.value, label: p.label }))} value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })} />
          </div>
          <div>
            <Label>Practice area</Label>
            <Select placeholder="Select…" options={PRACTICE_AREAS} value={editForm.practice_area} onChange={(e) => setEditForm({ ...editForm, practice_area: e.target.value })} />
          </div>
          <div>
            <Label>
              Fee arrangement
              <HelpTooltip>
                How this matter bills out. <strong>Hourly</strong>: log time, invoice at your hourly rate. <strong>Flat fee</strong>: fixed price regardless of hours. <strong>Contingency</strong>: percentage of recovery (typical for personal-injury matters). <strong>Retainer</strong>: client deposits funds into trust, draw down monthly.
              </HelpTooltip>
            </Label>
            <Select options={FEE_ARRANGEMENTS} value={editForm.fee_arrangement} onChange={(e) => setEditForm({ ...editForm, fee_arrangement: e.target.value })} />
          </div>
          <div>
            <Label>Jurisdiction</Label>
            {/* Same Ghana-jurisdictions dropdown the create form uses
                (see MattersPage.jsx). Switching from free text means
                editing an old matter with a non-canonical legacy
                value (e.g. "circuit court") will show no selection
                until the user picks one — that's intentional, it
                surfaces and resolves the inconsistency. */}
            <Select
              placeholder="Select court / forum…"
              options={GHANA_JURISDICTIONS}
              value={editForm.jurisdiction}
              onChange={(e) => setEditForm({ ...editForm, jurisdiction: e.target.value })}
            />
          </div>
          <div>
            <Label>Court</Label>
            <Input value={editForm.court} onChange={(e) => setEditForm({ ...editForm, court: e.target.value })} />
          </div>
          <div>
            <Label>Court case #</Label>
            <Input value={editForm.court_case_number} onChange={(e) => setEditForm({ ...editForm, court_case_number: e.target.value })} />
          </div>
          <div>
            <Label>Judge</Label>
            <Input value={editForm.judge} onChange={(e) => setEditForm({ ...editForm, judge: e.target.value })} />
          </div>
          <div>
            <Label>SOL date</Label>
            <Input type="date" value={editForm.statute_of_limitations} onChange={(e) => setEditForm({ ...editForm, statute_of_limitations: e.target.value })} />
          </div>
          <div>
            <Label>Budget</Label>
            <Input type="number" step="0.01" value={editForm.budget_amount} onChange={(e) => setEditForm({ ...editForm, budget_amount: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* ── Close matter modal ── */}
      <Modal
        open={showClose}
        onClose={() => setShowClose(false)}
        title="Close matter"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowClose(false)}>Cancel</Button>
            <Button loading={closing} onClick={handleClose} variant="danger" icon={CheckCircle2}>
              Close matter
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
            <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Closing a matter marks it as complete. All records are preserved and the matter remains fully viewable. You can reopen it at any time.
            </p>
          </div>

          <div>
            <Label required>Closing date</Label>
            <Input
              type="date"
              value={closeForm.close_date}
              onChange={(e) => setCloseForm((f) => ({ ...f, close_date: e.target.value }))}
            />
          </div>

          <div>
            <Label required>Reason for closing</Label>
            <Select
              value={closeForm.reason_code}
              onChange={(e) => setCloseForm((f) => ({ ...f, reason_code: e.target.value, closing_reason: '' }))}
              options={[
                { value: '', label: 'Select a reason…' },
                ...CLOSING_REASONS.map((r) => ({ value: r.value, label: r.label })),
              ]}
            />
          </div>

          {closeForm.reason_code && (
            <div>
              <Label>{closeForm.reason_code === 'other' ? 'Please describe the reason' : 'Additional notes (optional)'}</Label>
              <Textarea
                rows={3}
                placeholder={
                  closeForm.reason_code === 'other'
                    ? 'Describe the reason for closing…'
                    : 'e.g. Consent order filed 12 May 2026. File retained per retention policy until 2036.'
                }
                value={closeForm.closing_reason}
                onChange={(e) => setCloseForm((f) => ({ ...f, closing_reason: e.target.value }))}
              />
            </div>
          )}

          {(deadlines.filter((d) => d.status === 'open').length > 0 || tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length > 0) && (
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
              <input
                type="checkbox"
                className="mt-0.5 rounded"
                checked={closeForm.dismiss_open_items}
                onChange={(e) => setCloseForm((f) => ({ ...f, dismiss_open_items: e.target.checked }))}
              />
              <div>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Dismiss open deadlines & tasks</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {deadlines.filter((d) => d.status === 'open').length} open deadline{deadlines.filter((d) => d.status === 'open').length !== 1 ? 's' : ''} and{' '}
                  {tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length} pending task{tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length !== 1 ? 's' : ''} will be marked dismissed / cancelled.
                </p>
              </div>
            </label>
          )}
        </div>
      </Modal>

      {/* ── Reopen matter modal ── */}
      <Modal
        open={showReopen}
        onClose={() => setShowReopen(false)}
        title="Reopen matter"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowReopen(false)}>Cancel</Button>
            <Button loading={reopening} onClick={handleReopen} icon={FolderOpen}>
              Reopen matter
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 px-4 py-3">
            <FolderOpen size={16} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-indigo-800 dark:text-indigo-300">
              Reopening will set this matter back to <strong>Active</strong>. All existing records, time entries, and documents are preserved exactly as they were.
            </p>
          </div>

          {matter.closing_reason && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Previously closed because</p>
              <p className="text-sm text-slate-700 dark:text-slate-300">{matter.closing_reason}</p>
              {matter.closed_at && <p className="text-xs text-slate-400 mt-1">Closed {formatDate(matter.closed_at)}</p>}
            </div>
          )}

          <div>
            <Label required>Reason for reopening</Label>
            <Select
              value={reopenCode}
              onChange={(e) => { setReopenCode(e.target.value); setReopenOther(''); }}
              options={[
                { value: '', label: 'Select a reason…' },
                { value: 'New evidence or information received', label: 'New evidence or information received' },
                { value: 'Settlement fell through', label: 'Settlement fell through' },
                { value: 'Client reinstated instructions', label: 'Client reinstated instructions' },
                { value: 'Appeal filed', label: 'Appeal filed' },
                { value: 'Judgment being enforced', label: 'Judgment being enforced' },
                { value: 'Closed in error', label: 'Closed in error' },
                { value: 'Court order to continue proceedings', label: 'Court order to continue proceedings' },
                { value: 'Other', label: 'Other' },
              ]}
            />
            {reopenCode === 'Other' && (
              <Textarea
                className="mt-2"
                rows={2}
                placeholder="Describe why this matter is being reopened…"
                value={reopenOther}
                onChange={(e) => setReopenOther(e.target.value)}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* ── Log time modal ── */}
      <Modal
        open={showLogTime}
        onClose={() => setShowLogTime(false)}
        title={`Log time — ${matter.title}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowLogTime(false)}>Cancel</Button>
            <Button onClick={saveTime}>Save entry</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Description</Label>
            <Input value={timeForm.description} onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })} placeholder="e.g. Drafted motion to dismiss" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Hours</Label>
              <Input type="number" min="0" value={timeForm.hours} onChange={(e) => setTimeForm({ ...timeForm, hours: e.target.value })} placeholder="0" />
            </div>
            <div>
              <Label>Minutes</Label>
              <Input type="number" min="0" max="59" value={timeForm.minutes} onChange={(e) => setTimeForm({ ...timeForm, minutes: e.target.value })} placeholder="0" />
            </div>
            <div>
              <Label>Hourly rate (GHS)</Label>
              <Input type="number" step="0.01" value={timeForm.hourly_rate} onChange={(e) => setTimeForm({ ...timeForm, hourly_rate: e.target.value })} placeholder={userProfile?.hourly_rate || '0'} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={timeForm.entry_date} onChange={(e) => setTimeForm({ ...timeForm, entry_date: e.target.value })} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={timeForm.billable} onChange={(e) => setTimeForm({ ...timeForm, billable: e.target.checked })} className="rounded" />
                Billable
              </label>
            </div>
          </div>
          {(timeForm.hours || timeForm.minutes) && (
            <p className="text-xs text-slate-500">
              Total: {timeForm.hours || 0}h {timeForm.minutes || 0}m
              {timeForm.hourly_rate && ` · GHS ${((parseInt(timeForm.hours || 0) * 60 + parseInt(timeForm.minutes || 0)) / 60 * parseFloat(timeForm.hourly_rate)).toFixed(2)}`}
            </p>
          )}
        </div>
      </Modal>

      {/* ── Manage Team modal ── */}
      <ManageTeamModal
        open={showManageTeam}
        onClose={() => setShowManageTeam(false)}
        firmId={firmId}
        matterId={matterId}
        firmUsers={firmUsers}
        employees={employees}
        matterUsers={matterUsers}
        matterTeam={matterTeam}
        setMatterUsers={setMatterUsers}
        setMatterTeam={setMatterTeam}
        leadAttorneyId={matter.responsible_attorney_id}
      />
    </div>
  );
}

function ManageTeamModal({
  open, onClose, firmId, matterId,
  firmUsers, employees,
  matterUsers, matterTeam,
  setMatterUsers, setMatterTeam,
  leadAttorneyId,
}) {
  const [working, setWorking] = useState({});

  // Sets of already-assigned IDs for quick lookup
  const assignedUserIds = new Set(matterUsers.map((mu) => mu.user_id));
  const assignedEmpIds  = new Set(matterTeam.map((mt) => mt.employee_id));

  // ── Login user handlers ──
  const handleAddUser = async (user) => {
    setWorking((w) => ({ ...w, [`u:${user.id}`]: true }));
    const isLead = user.id === leadAttorneyId;
    const { data, error } = await addMatterUser(firmId, matterId, user.id, isLead ? 'lead_attorney' : 'team_member');
    if (!error && data) setMatterUsers((prev) => [...prev, data]);
    setWorking((w) => ({ ...w, [`u:${user.id}`]: false }));
  };

  const handleRemoveUser = async (mu) => {
    setWorking((w) => ({ ...w, [`u:${mu.user_id}`]: true }));
    const { error } = await removeMatterUser(mu.id);
    if (!error) setMatterUsers((prev) => prev.filter((m) => m.id !== mu.id));
    setWorking((w) => ({ ...w, [`u:${mu.user_id}`]: false }));
  };

  // ── Employee handlers ──
  const handleAddEmployee = async (emp) => {
    setWorking((w) => ({ ...w, [`e:${emp.id}`]: true }));
    const { data, error } = await addMatterTeamMember(firmId, matterId, emp.id, 'team_member');
    if (!error && data) setMatterTeam((prev) => [...prev, data]);
    setWorking((w) => ({ ...w, [`e:${emp.id}`]: false }));
  };

  const handleRemoveEmployee = async (mt) => {
    setWorking((w) => ({ ...w, [`e:${mt.employee_id}`]: true }));
    const { error } = await removeMatterTeamMember(mt.id);
    if (!error) setMatterTeam((prev) => prev.filter((m) => m.id !== mt.id));
    setWorking((w) => ({ ...w, [`e:${mt.employee_id}`]: false }));
  };

  // Merge all staff alphabetically for display
  const allStaff = [
    ...(firmUsers || []).map((u) => ({ ...u, _source: 'user',     _key: `u:${u.id}`, _role: u.role     })),
    ...(employees || []).map((e) => ({ ...e, _source: 'employee', _key: `e:${e.id}`, _role: e.role || e.title })),
  ].sort((a, b) =>
    (a.last_name || '').localeCompare(b.last_name || '') ||
    (a.first_name || '').localeCompare(b.first_name || '')
  );

  const hasStaff = allStaff.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Manage case team" size="md"
      footer={<Button onClick={onClose}>Done</Button>}>
      <div className="space-y-1">
        {!hasStaff && (
          <p className="text-sm text-slate-500 py-4 text-center">No staff found. Add employees in the Employees section.</p>
        )}
        {allStaff.map((person) => {
          const isUser     = person._source === 'user';
          const isEmployee = person._source === 'employee';
          const assigned   = isUser ? assignedUserIds.has(person.id) : assignedEmpIds.has(person.id);
          const mu         = isUser ? matterUsers.find((m) => m.user_id === person.id) : null;
          const mt         = isEmployee ? matterTeam.find((m) => m.employee_id === person.id) : null;
          const isLead     = mu?.role_on_matter === 'lead_attorney';
          const busy       = working[person._key];

          return (
            <div key={person._key} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
              <div className={`w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0 ${
                isUser
                  ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300'
                  : 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
              }`}>
                {(person.first_name?.[0] || '') + (person.last_name?.[0] || '')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{fullName(person) || person.email}</p>
                <p className="text-xs text-slate-500">{person._role || '—'}</p>
              </div>
              {isLead && <Badge tone="indigo">Lead</Badge>}
              {isEmployee && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex-shrink-0">Staff</span>
              )}
              {assigned ? (
                <Button size="sm" variant="secondary" loading={busy}
                  onClick={() => isUser ? handleRemoveUser(mu) : handleRemoveEmployee(mt)}>
                  Remove
                </Button>
              ) : (
                <Button size="sm" loading={busy}
                  onClick={() => isUser ? handleAddUser(person) : handleAddEmployee(person)}>
                  Add
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
    <dd className="text-sm font-medium text-slate-900 dark:text-slate-100 mt-0.5">{value || '—'}</dd>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

// ─── Timeline ────────────────────────────────────────────────────────────────
const TL_CONFIG = {
  time:     { bg: 'bg-blue-500',    ring: 'ring-blue-200 dark:ring-blue-900',    label: 'Time logged',         Icon: Clock },
  deadline: { bg: 'bg-red-500',     ring: 'ring-red-200 dark:ring-red-900',      label: 'Deadline',            Icon: AlertCircle },
  task:     { bg: 'bg-emerald-500', ring: 'ring-emerald-200 dark:ring-emerald-900', label: 'Task',             Icon: CheckCircle2 },
  document: { bg: 'bg-slate-400',   ring: 'ring-slate-200 dark:ring-slate-700',  label: 'Document uploaded',   Icon: FileText },
};

const Timeline = ({ timeEntries, deadlines, tasks, documents }) => {
  const events = useMemo(() => {
    const all = [
      ...timeEntries.map((e) => ({ type: 'time',     date: e.entry_date  || e.created_at, data: e })),
      ...deadlines.map((d)   => ({ type: 'deadline', date: d.due_date    || d.created_at, data: d })),
      ...tasks.map((t)       => ({ type: 'task',     date: t.created_at,                  data: t })),
      ...documents.map((d)   => ({ type: 'document', date: d.created_at,                  data: d })),
    ];
    return all.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [timeEntries, deadlines, tasks, documents]);

  const bodyText = (ev) => {
    if (ev.type === 'time')     return `${ev.data.description || 'No description'} · ${formatDuration(ev.data.duration_minutes)}${ev.data.billable ? ' · billable' : ''}`;
    if (ev.type === 'deadline') return `${ev.data.title}`;
    if (ev.type === 'task')     return `${ev.data.title}`;
    if (ev.type === 'document') return `${ev.data.name}`;
    return '';
  };

  const chipText = (ev) => {
    if (ev.type === 'deadline') return ev.data.status;
    if (ev.type === 'task')     return ev.data.status?.replace('_', ' ');
    if (ev.type === 'time')     return formatMoney(ev.data.amount || 0);
    return null;
  };

  if (!events.length) return (
    <Card><CardBody><EmptyState icon={CalendarIcon} title="No activity yet" description="Time entries, deadlines, tasks and documents will appear here." /></CardBody></Card>
  );

  return (
    <Card>
      <CardBody>
        <div className="relative">
          {/* Vertical spine */}
          <div className="absolute left-[19px] top-4 bottom-4 w-px bg-slate-200 dark:bg-slate-700" />

          <ul className="space-y-0">
            {events.map((ev, i) => {
              const cfg = TL_CONFIG[ev.type];
              const Icon = cfg.Icon;
              return (
                <li key={i} className="relative flex items-start gap-4 pb-6 last:pb-0">
                  {/* Dot */}
                  <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full ${cfg.bg} ring-4 ${cfg.ring} flex items-center justify-center`}>
                    <Icon size={15} className="text-white" />
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{cfg.label}</span>
                      {chipText(ev) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-medium capitalize">
                          {chipText(ev)}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{formatDate(ev.date)}</span>
                    </div>
                    <p className="text-sm text-slate-800 dark:text-slate-200 mt-0.5 font-medium">{bodyText(ev)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </CardBody>
    </Card>
  );
};

// ─── Parties ─────────────────────────────────────────────────────────────────
const ContactCard = ({ contact, role, onInvite, inviting }) => {
  if (!contact) return null;
  const isClient = role === 'Client';
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center flex-shrink-0">
              <User size={16} className="text-indigo-600 dark:text-indigo-300" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{fullName(contact) || contact.organization || '—'}</p>
              <p className="text-xs text-slate-500">{role}</p>
            </div>
          </div>
          {isClient && contact.email && onInvite && (
            <button
              onClick={onInvite}
              disabled={inviting}
              title={contact.portal_enabled ? 'Re-send portal invite' : 'Invite to client portal'}
              className="flex-shrink-0 text-xs px-2.5 py-1.5 rounded-lg border font-medium transition
                text-indigo-600 border-indigo-200 hover:bg-indigo-50
                dark:text-indigo-400 dark:border-indigo-800 dark:hover:bg-indigo-950
                disabled:opacity-50"
            >
              {inviting ? 'Sending…' : contact.portal_enabled ? 'Re-invite' : 'Invite to portal'}
            </button>
          )}
        </div>
        <div className="space-y-1.5 text-sm">
          {contact.organization && <Row label={<Globe size={12} />} value={contact.organization} />}
          {contact.email        && <Row label={<Mail size={12} />}  value={contact.email} />}
          {contact.phone        && <Row label={<Phone size={12} />} value={contact.phone} />}
          {contact.address      && <Row label={<MapPin size={12} />} value={contact.address} />}
        </div>
        {isClient && contact.portal_enabled && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircle2 size={11} /> Portal access active
          </p>
        )}
      </CardBody>
    </Card>
  );
};

const PartiesTab = ({ matter, setMatter, opposingCounsel, setOpposingCounsel, firmId, matterId }) => {
  const [contacts,     setContacts]     = useState([]);
  const [loadingC,     setLoadingC]     = useState(true);
  const [editingParty, setEditingParty] = useState(null); // 'client' | 'opposing'
  const [pickedId,     setPickedId]     = useState('');
  const [saving,       setSaving]       = useState(false);
  const [inviting,     setInviting]     = useState(false);
  const [inviteMsg,    setInviteMsg]    = useState('');

  const handlePortalInvite = async () => {
    if (!matter.client?.id) return;
    setInviting(true);
    setInviteMsg('');
    const { error } = await invitePortalClient(matter.client.id);
    if (error) {
      setInviteMsg(`Error: ${error.message}`);
    } else {
      setMatter((m) => ({
        ...m,
        client: { ...m.client, portal_enabled: true, portal_invited_at: new Date().toISOString() },
      }));
      setInviteMsg('Invite sent! The client will receive an email with portal access.');
    }
    setInviting(false);
    setTimeout(() => setInviteMsg(''), 5000);
  };

  useEffect(() => {
    if (!firmId) return;
    listContacts(firmId)
      .then(({ data }) => setContacts(data || []))
      .catch((err) => console.error('MatterDetailPage contacts fetch error:', err))
      .finally(() => setLoadingC(false));
  }, [firmId]);

  const openPicker = (party) => {
    setPickedId(party === 'client' ? (matter.client_id || '') : (matter.opposing_counsel_id || ''));
    setEditingParty(party);
  };

  const savePicker = async () => {
    setSaving(true);
    const field = editingParty === 'client' ? 'client_id' : 'opposing_counsel_id';
    const { error } = await updateMatter(matterId, { [field]: pickedId || null });
    if (error) { setSaving(false); toast.error(error.message); return; }
    // Update local state
    if (editingParty === 'client') {
      const chosen = contacts.find((c) => c.id === pickedId) || null;
      setMatter((m) => ({ ...m, client_id: pickedId || null, client: chosen }));
    } else {
      const chosen = contacts.find((c) => c.id === pickedId) || null;
      setOpposingCounsel(chosen);
      setMatter((m) => ({ ...m, opposing_counsel_id: pickedId || null }));
    }
    setSaving(false);
    setEditingParty(null);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Client card */}
        <div className="relative group/card">
          {inviteMsg && (
            <p className={`text-xs mb-2 px-1 ${inviteMsg.startsWith('Error') ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {inviteMsg}
            </p>
          )}
          {matter.client
            ? <ContactCard contact={matter.client} role="Client" onInvite={handlePortalInvite} inviting={inviting} />
            : (
              <Card>
                <CardBody>
                  <EmptyState icon={User} title="No client assigned"
                    description="Assign a client to this matter."
                    action={<Button size="sm" icon={Pencil} onClick={() => openPicker('client')}>Assign client</Button>} />
                </CardBody>
              </Card>
            )}
          {matter.client && (
            <button
              onClick={() => openPicker('client')}
              className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity p-1.5 rounded-lg bg-white dark:bg-slate-800 shadow border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-indigo-600"
              title="Change client">
              <Pencil size={13} />
            </button>
          )}
        </div>

        {/* Opposing Counsel card */}
        <div className="relative group/card">
          {opposingCounsel
            ? <ContactCard contact={opposingCounsel} role="Opposing Counsel" />
            : (
              <Card>
                <CardBody>
                  <EmptyState icon={Users} title="No opposing counsel"
                    description="Assign the opposing counsel from your contacts."
                    action={<Button size="sm" icon={Plus} onClick={() => openPicker('opposing')}>Assign opposing counsel</Button>} />
                </CardBody>
              </Card>
            )}
          {opposingCounsel && (
            <button
              onClick={() => openPicker('opposing')}
              className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity p-1.5 rounded-lg bg-white dark:bg-slate-800 shadow border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-indigo-600"
              title="Change opposing counsel">
              <Pencil size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Pick contact modal */}
      <Modal
        open={!!editingParty}
        onClose={() => setEditingParty(null)}
        title={editingParty === 'client' ? 'Assign client' : 'Assign opposing counsel'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditingParty(null)}>Cancel</Button>
            <Button onClick={savePicker} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </>
        }
      >
        {loadingC ? (
          <p className="text-sm text-slate-400 py-4">Loading contacts…</p>
        ) : (
          <div className="space-y-3">
            <Label>{editingParty === 'client' ? 'Select client' : 'Select opposing counsel'}</Label>
            <Select
              value={pickedId}
              onChange={(e) => setPickedId(e.target.value)}
              options={[
                { value: '', label: '— None —' },
                ...contacts.map((c) => ({
                  value: c.id,
                  label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.organization || c.email,
                })),
              ]}
            />
            {pickedId && (() => {
              const c = contacts.find((x) => x.id === pickedId);
              if (!c) return null;
              return (
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm space-y-1">
                  {c.organization && <p className="font-medium">{c.organization}</p>}
                  {(c.phone || c.email) && <p className="text-slate-500">{[c.phone, c.email].filter(Boolean).join(' · ')}</p>}
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
    </>
  );
};

// ─── Billing ─────────────────────────────────────────────────────────────────
const FEE_ARRANGEMENTS_BILLING = [
  { value: 'flat_fee',    label: 'Flat Fee' },
  { value: 'hourly',      label: 'Hourly' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'retainer',    label: 'Retainer' },
  { value: 'pro_bono',    label: 'Pro Bono' },
];
const PAYMENT_METHODS_BILLING = [
  { value: 'cash',        label: 'Cash' },
  { value: 'check',       label: 'Cheque' },
  { value: 'ach',         label: 'Bank Transfer' },
  { value: 'credit_card', label: 'Credit / Debit Card' },
  { value: 'wire',        label: 'Wire Transfer' },
  { value: 'mobile',      label: 'Mobile Money' },
  { value: 'other',       label: 'Other' },
];
const CHARGE_TYPES_BILLING = [
  { value: 'disbursement', label: 'Disbursement' },
  { value: 'court_fee',    label: 'Court Fee' },
  { value: 'filing_fee',   label: 'Filing Fee' },
  { value: 'travel',       label: 'Travel' },
  { value: 'expert_fee',   label: 'Expert Fee' },
  { value: 'other',        label: 'Other' },
];
const todayStr = () => new Date().toISOString().split('T')[0];
const INVOICE_TONE = { draft: 'slate', sent: 'blue', viewed: 'indigo', partial: 'amber', paid: 'green', overdue: 'red', void: 'slate' };

const BillingTab = ({ invoices: invoicesProp, setInvoices, matter, firmId }) => {
  const { firm: firmData } = useFirm();
  const [loading,        setLoading]        = useState(true);
  const [payments,       setPayments]       = useState([]);
  const [charges,        setCharges]        = useState([]);
  // Own local invoice state — refreshed from DB on every mount so invoices
  // created from the main Billing page always appear here.
  const [invoices,       setLocalInvoices]  = useState(invoicesProp || []);

  const syncInvoices = (list) => {
    setLocalInvoices(list);
    setInvoices(list); // keep parent in sync for cross-tab consistency
  };
  const [feeForm,      setFeeForm]      = useState({ agreed_fee: matter?.agreed_fee || '', fee_arrangement: matter?.fee_arrangement || 'flat_fee' });
  const [savingFee,    setSavingFee]    = useState(false);
  const [feeSuccess,   setFeeSuccess]   = useState(false);
  const [showPayForm,  setShowPayForm]  = useState(false);
  const [payForm,      setPayForm]      = useState({ amount: '', payment_date: todayStr(), method: 'cash', payment_label: '', notes: '' });
  const [savingPay,    setSavingPay]    = useState(false);
  const [linkedCharges,  setLinkedCharges]  = useState([]); // charge ids to mark paid
  const [linkedInvoices, setLinkedInvoices] = useState([]); // invoice ids to mark paid
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [chargeForm,   setChargeForm]   = useState({ description: '', amount: '', charge_date: todayStr(), charge_type: 'disbursement', notes: '' });
  const [savingCharge, setSavingCharge] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);  // charge object being edited
  const [editChargeForm, setEditChargeForm] = useState({});
  const [savingEditCharge, setSavingEditCharge] = useState(false);
  const [deletingPayment,  setDeletingPayment]  = useState(null);
  const [deletingCharge,   setDeletingCharge]   = useState(null);
  const [deletingInvoice,  setDeletingInvoice]  = useState(null);
  const [sharingInvoice,   setSharingInvoice]   = useState(null); // invoice being shared
  const [linkCopied,       setLinkCopied]       = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData,      setReceiptData]      = useState(null);
  const [viewingReceipt,   setViewingReceipt]   = useState(null); // past payment for receipt re-view

  // ── Add-invoice modal state ──
  const todayIso  = () => new Date().toISOString().split('T')[0];
  const dueIn30   = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; };
  const blankInv  = (num) => ({ invoice_number: num, issue_date: todayIso(), due_date: dueIn30(), status: 'draft', notes: '', tax_rate: 0, discount_amount: 0 });
  const blankLine = () => ({ _id: Math.random().toString(36).slice(2), item_type: 'flat_fee', description: '', quantity: 1, rate: '', amount: 0 });
  const LINE_TYPES = [{ value: 'flat_fee', label: 'Flat Fee' }, { value: 'time', label: 'Time' }, { value: 'expense', label: 'Expense' }, { value: 'adjustment', label: 'Adjustment' }, { value: 'discount', label: 'Discount' }, { value: 'pro_bono', label: 'Pro Bono' }];
  const [showAddInvoice,   setShowAddInvoice]   = useState(false);
  const [invForm,          setInvForm]          = useState(blankInv('INV-001'));
  const [invLines,         setInvLines]         = useState([blankLine()]);
  const [savingInvoice,    setSavingInvoice]    = useState(false);
  const [invSelectedLevies, setInvSelectedLevies] = useState([]);
  const [invCustomTaxRate,  setInvCustomTaxRate]  = useState(0);

  const invSubtotal = invLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const invTaxAmt   = invSubtotal * ((parseFloat(invForm.tax_rate) || 0) / 100);
  const invDiscount = parseFloat(invForm.discount_amount) || 0;
  const invTotal    = Math.max(invSubtotal + invTaxAmt - invDiscount, 0);

  const openAddInvoice = async () => {
    let nextNum = nextInvoiceNumber([]);
    try {
      const { data: allInv } = await listInvoices(firmId);
      nextNum = nextInvoiceNumber(allInv || []);
    } catch (_) { /* fall back to default */ }
    setInvForm(blankInv(nextNum));
    setInvLines([blankLine()]);
    setInvSelectedLevies([]);
    setInvCustomTaxRate(0);
    setShowAddInvoice(true);
  };

  const submitNewInvoice = async (e) => {
    e.preventDefault();
    setSavingInvoice(true);
    const { data, error } = await createInvoice(firmId, {
      ...invForm,
      client_id:       matter.client_id || null,
      matter_id:       matter.id,
      tax_rate:        parseFloat(invForm.tax_rate) || 0,
      discount_amount: invDiscount,
      subtotal:        invSubtotal,
      tax_amount:      invTaxAmt,
      total:           invTotal,
    });
    if (error) { setSavingInvoice(false); toast.error(error.message); return; }
    const validLines = invLines.filter((l) => l.description.trim());
    if (validLines.length > 0) {
      await createInvoiceLineItems(validLines.map(({ _id, ...l }, i) => ({
        invoice_id:  data.id,
        item_type:   l.item_type,
        description: l.description,
        quantity:    parseFloat(l.quantity) || 1,
        rate:        parseFloat(l.rate) || 0,
        amount:      parseFloat(l.amount) || 0,
        sort_order:  i,
      })));
    }
    setSavingInvoice(false);
    setLocalInvoices((prev) => [{ ...data, subtotal: invSubtotal, tax_amount: invTaxAmt, total: invTotal }, ...prev]);
    setShowAddInvoice(false);
  };

  const updateLine = (id, field, val) => {
    setInvLines((prev) => prev.map((l) => {
      if (l._id !== id) return l;
      const updated = { ...l, [field]: val };
      if (field === 'quantity' || field === 'rate') {
        updated.amount = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.rate) || 0);
      }
      return updated;
    }));
  };

  useEffect(() => {
    if (!matter?.id || !firmId) return;
    (async () => {
      try {
        const [{ data: p }, { data: c }, invRes] = await Promise.all([
          listMatterPayments(matter.id),
          listCaseCharges(matter.id),
          listInvoices(firmId),
        ]);
        setPayments(p || []);
        setCharges(c || []);
        // Filter all firm invoices client-side — guarantees we catch invoices
        // created from the main Billing page that share this matter_id
        const matterInvoices = (invRes.data || []).filter(
          (i) => i.matter_id === matter.id
        );
        syncInvoices(matterInvoices);
      } catch (err) {
        console.error('BillingTab fetch error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [matter?.id, firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  const agreedFee  = Number(feeForm.agreed_fee) || 0;
  const totalPaid  = payments.reduce((s, p) => s + Number(p.amount), 0);
  const totalExtra = charges.reduce((s, c) => s + Number(c.amount), 0);
  const totalOwed  = agreedFee + totalExtra;
  const balance    = Math.max(totalOwed - totalPaid, 0);
  const paidPct    = totalOwed > 0 ? Math.min((totalPaid / totalOwed) * 100, 100) : 0;
  const barColor   = paidPct >= 100 ? 'bg-emerald-500' : paidPct >= 50 ? 'bg-amber-400' : paidPct > 0 ? 'bg-orange-400' : 'bg-slate-300';

  const saveFee = async (e) => {
    e.preventDefault(); setSavingFee(true);
    await updateMatterBilling(matter.id, { agreed_fee: agreedFee, fee_arrangement: feeForm.fee_arrangement });
    setSavingFee(false); setFeeSuccess(true);
    setTimeout(() => setFeeSuccess(false), 2000);
  };

  const savePayment = async (e) => {
    e.preventDefault(); setSavingPay(true);
    const receiptNum = nextReceiptNumber(payments);
    const { data, error } = await recordInstallmentPayment(firmId, {
      matter_id: matter.id, client_id: matter.client_id || null,
      amount: Number(payForm.amount), payment_date: payForm.payment_date,
      method: payForm.method, payment_label: payForm.payment_label || null,
      notes: payForm.notes || null, receipt_number: receiptNum,
    });
    setSavingPay(false);
    if (error) { toast.error(error.message); return; }
    setPayments((prev) => [data, ...prev]);
    // Snapshot linked items for receipt BEFORE state updates
    const linkedChargeObjs  = charges.filter((c)   => linkedCharges.includes(c.id));
    const linkedInvoiceObjs = invoices.filter((inv) => linkedInvoices.includes(inv.id));
    // Mark linked charges as paid
    if (linkedCharges.length > 0) {
      await Promise.all(linkedCharges.map((id) => markCaseChargePaid(id, true)));
      setCharges((prev) => prev.map((c) => linkedCharges.includes(c.id) ? { ...c, status: 'paid' } : c));
    }
    // Mark linked invoices as paid
    if (linkedInvoices.length > 0) {
      await Promise.all(linkedInvoices.map((id) => updateInvoice(id, { status: 'paid' })));
      setLocalInvoices((prev) => prev.map((inv) => linkedInvoices.includes(inv.id) ? { ...inv, status: 'paid' } : inv));
    }
    // Build receipt data and show modal
    const rec = {
      receiptNumber:  receiptNum,
      firm:           firmData || {},
      client:         matter.client || {},
      matterTitle:    matter.title || matter.matter_number || '',
      amount:         Number(payForm.amount),
      payment_date:   payForm.payment_date,
      method:         payForm.method,
      payment_label:  payForm.payment_label || '',
      notes:          payForm.notes || '',
      linkedCharges:  linkedChargeObjs.map((c) => ({ description: c.description, amount: c.amount })),
      linkedInvoices: linkedInvoiceObjs.map((inv) => ({ invoice_number: inv.invoice_number, total: inv.total })),
    };
    setReceiptData(rec);
    setShowReceiptModal(true);
    setPayForm({ amount: '', payment_date: todayStr(), method: 'cash', payment_label: '', notes: '' });
    setLinkedCharges([]);
    setLinkedInvoices([]);
    setShowPayForm(false);
  };

  const saveCharge = async (e) => {
    e.preventDefault(); setSavingCharge(true);
    const { data, error } = await addCaseCharge(firmId, {
      matter_id: matter.id, client_id: matter.client_id || null,
      description: chargeForm.description, amount: Number(chargeForm.amount),
      charge_date: chargeForm.charge_date, charge_type: chargeForm.charge_type, notes: chargeForm.notes || null,
    });
    setSavingCharge(false);
    if (error) { toast.error(error.message); return; }
    setCharges((prev) => [data, ...prev]);
    setChargeForm({ description: '', amount: '', charge_date: todayStr(), charge_type: 'disbursement', notes: '' });
    setShowChargeForm(false);
  };

  const openEditCharge = (c) => {
    setEditingCharge(c);
    setEditChargeForm({ description: c.description, amount: c.amount, charge_date: c.charge_date, charge_type: c.charge_type, notes: c.notes || '' });
  };

  const saveEditCharge = async (e) => {
    e.preventDefault(); setSavingEditCharge(true);
    const { data, error } = await updateCaseCharge(editingCharge.id, {
      description: editChargeForm.description,
      amount: Number(editChargeForm.amount),
      charge_date: editChargeForm.charge_date,
      charge_type: editChargeForm.charge_type,
      notes: editChargeForm.notes || null,
    });
    setSavingEditCharge(false);
    if (error) { toast.error(error.message); return; }
    setCharges((prev) => prev.map((c) => c.id === editingCharge.id ? data : c));
    setEditingCharge(null);
  };

  const removePayment = async (p) => {
    setDeletingPayment(p);
  };

  // eslint-disable-next-line no-unused-vars
  const _handleDeletePayment = async () => {
    if (!deletingPayment) return;
    await deletePayment(deletingPayment.id);
    setPayments((prev) => prev.filter((x) => x.id !== deletingPayment.id));
    setDeletingPayment(null);
  };

  const removeCharge = async (c) => {
    setDeletingCharge(c);
  };

  // eslint-disable-next-line no-unused-vars
  const _handleDeleteCharge = async () => {
    if (!deletingCharge) return;
    await deleteCaseCharge(deletingCharge.id);
    setCharges((prev) => prev.filter((x) => x.id !== deletingCharge.id));
    setDeletingCharge(null);
  };

  const removeInvoice = async (inv) => {
    setDeletingInvoice(inv);
  };

  const RECEIPT_METHOD_LABELS = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', mobile_money: 'Mobile Money', card: 'Card', other: 'Other' };
  const buildReceiptFromPayment = (p) => ({
    receiptNumber:  p.receipt_number || `REC-${(p.id || '').slice(0, 8).toUpperCase()}`,
    firm:           firmData || {},
    client:         matter.client || {},
    matterTitle:    matter.title || matter.matter_number || '',
    amount:         Number(p.amount),
    payment_date:   p.payment_date,
    method:         p.method,
    payment_label:  p.payment_label || '',
    notes:          p.notes || '',
    linkedCharges:  [],
    linkedInvoices: [],
  });

  const invoiceShareUrl = (inv) => {
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#/invoice/${inv.share_token}`;
  };

  const downloadInvoice = async (inv) => {
    const { data } = await getInvoice(inv.id);
    if (!data) { toast.error('Could not load invoice data.'); return; }
    printInvoice({
      ...data,
      firm:       {
        name:     firmData?.name     || '',
        email:    firmData?.email    || '',
        phone:    firmData?.phone    || '',
        address:  firmData?.address  || '',
        logo_url: firmData?.logo_url || '',
      },
      client:     data.client || {},
      line_items: data.line_items || [],
    });
  };

  const copyInvoiceLink = (inv) => {
    navigator.clipboard.writeText(invoiceShareUrl(inv));
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const openWhatsApp = (inv) => {
    const clientPhone = matter?.client?.phone || '';
    const phone = clientPhone.replace(/\D/g, '');
    const url   = invoiceShareUrl(inv);
    const text  = encodeURIComponent(
      `Hello, please find your invoice ${inv.invoice_number} here:\n${url}`
    );
    window.open(`https://wa.me/${phone ? phone : ''}?text=${text}`, '_blank');
  };

  // eslint-disable-next-line no-unused-vars
  const _handleDeleteInvoice = async () => {
    if (!deletingInvoice) return;
    const { error } = await deleteInvoice(deletingInvoice.id);
    if (error) { toast.error(error.message); setDeletingInvoice(null); return; }
    setLocalInvoices((prev) => prev.filter((i) => i.id !== deletingInvoice.id));
    setDeletingInvoice(null);
  };

  const changeInvoiceStatus = async (inv, newStatus) => {
    const { error } = await updateInvoice(inv.id, { status: newStatus });
    if (error) { toast.error(error.message); return; }
    setLocalInvoices((prev) => prev.map((i) => i.id === inv.id ? { ...i, status: newStatus } : i));
    if (newStatus === 'sent') toast.success(`Invoice ${inv.invoice_number} sent to client.`);
  };
  return (
    <div className="space-y-6">

      {/* ── Summary bar ── */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100 dark:divide-slate-800">
          {[
            { label: 'Agreed Fee',    value: formatMoney(agreedFee),              sub: FEE_ARRANGEMENTS_BILLING.find(f => f.value === feeForm.fee_arrangement)?.label },
            { label: 'Extra Charges', value: formatMoney(totalExtra),             sub: `${charges.length} charge${charges.length !== 1 ? 's' : ''}`, tone: totalExtra > 0 ? 'amber' : null },
            { label: 'Total Paid',    value: formatMoney(totalPaid),              sub: `${payments.length} payment${payments.length !== 1 ? 's' : ''}`, tone: 'green' },
            { label: 'Balance Due',   value: formatMoney(balance),                sub: balance === 0 && totalOwed > 0 ? 'Fully paid ✓' : 'Outstanding', tone: balance === 0 && totalOwed > 0 ? 'green' : balance > 0 ? 'red' : null },
          ].map(({ label, value, sub, tone }) => (
            <div key={label} className="p-5 text-center">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
              <p className={`text-xl font-bold ${tone === 'green' ? 'text-emerald-600' : tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-800 dark:text-white'}`}>{value}</p>
              {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
            </div>
          ))}
        </div>
        {totalOwed > 0 && (
          <div className="px-5 pb-4 pt-1 bg-slate-50 dark:bg-slate-800/40">
            <div className="flex justify-between text-xs text-slate-500 mb-1.5">
              <span>Payment progress</span><span className="font-semibold">{Math.round(paidPct)}%</span>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${paidPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Fee agreement ── */}
      <Card>
        <CardBody>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2 mb-3">
            <Scale size={14} /> Fee Agreement
          </p>
          <form onSubmit={saveFee} className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Arrangement</label>
              <Select value={feeForm.fee_arrangement} onChange={(e) => setFeeForm(f => ({ ...f, fee_arrangement: e.target.value }))} options={FEE_ARRANGEMENTS_BILLING} />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Agreed Total (GHS)</label>
              <Input type="number" min="0" step="0.01" value={feeForm.agreed_fee} onChange={(e) => setFeeForm(f => ({ ...f, agreed_fee: e.target.value }))} placeholder="0.00" />
            </div>
            <Button type="submit" variant="secondary" disabled={savingFee}>
              {feeSuccess ? <><CheckCircle2 size={13} className="text-emerald-500 inline mr-1" />Saved</> : savingFee ? 'Saving…' : 'Save fee'}
            </Button>
          </form>
        </CardBody>
      </Card>

      {/* ── Installment payments ── */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Banknote size={14} /> Installment Payments
            </p>
            <Button size="sm" icon={Plus} onClick={() => setShowPayForm(v => !v)}>Record payment</Button>
          </div>

          {showPayForm && (
            <form onSubmit={savePayment} className="mb-4 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Amount (GHS)</label>
                  <Input type="number" min="0.01" step="0.01" value={payForm.amount} onChange={(e) => setPayForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Payment date</label>
                  <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm(f => ({ ...f, payment_date: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Method</label>
                  <Select value={payForm.method} onChange={(e) => setPayForm(f => ({ ...f, method: e.target.value }))} options={PAYMENT_METHODS_BILLING} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Label (e.g. "1st instalment")</label>
                  <Input value={payForm.payment_label} onChange={(e) => setPayForm(f => ({ ...f, payment_label: e.target.value }))} placeholder="Optional" />
                </div>
              </div>
              {/* Apply to charges / invoices */}
              {(charges.filter(c => c.status !== 'paid').length > 0 || invoices.filter(i => i.status !== 'paid').length > 0) && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-2">Apply to (mark as paid)</label>
                  <div className="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
                    {charges.filter(c => c.status !== 'paid').map(c => (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <input type="checkbox" checked={linkedCharges.includes(c.id)}
                          onChange={() => setLinkedCharges(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300 truncate">{c.description}</p>
                          <p className="text-xs text-slate-400">{formatDate(c.charge_date)} · Charge</p>
                        </div>
                        <span className="text-xs font-mono font-semibold text-amber-600">{formatMoney(c.amount)}</span>
                      </label>
                    ))}
                    {invoices.filter(i => i.status !== 'paid').map(inv => (
                      <label key={inv.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <input type="checkbox" checked={linkedInvoices.includes(inv.id)}
                          onChange={() => setLinkedInvoices(prev => prev.includes(inv.id) ? prev.filter(x => x !== inv.id) : [...prev, inv.id])}
                          className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300 truncate">{inv.invoice_number}</p>
                          <p className="text-xs text-slate-400">{formatDate(inv.issue_date)} · Invoice</p>
                        </div>
                        <span className="text-xs font-mono font-semibold text-slate-600 dark:text-slate-300">{formatMoney(inv.total || 0)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => { setShowPayForm(false); setLinkedCharges([]); setLinkedInvoices([]); }}>Cancel</Button>
                <Button type="submit" disabled={savingPay}>{savingPay ? 'Saving…' : 'Record payment'}</Button>
              </div>
            </form>
          )}

          {loading ? <p className="text-sm text-slate-400 py-2">Loading…</p>
            : payments.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">No payments recorded yet</p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 group">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                      <ArrowDownCircle size={14} className="text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{p.payment_label || PAYMENT_METHODS_BILLING.find(m => m.value === p.method)?.label || p.method}</p>
                        {p.receipt_number && (
                          <span className="text-xs text-slate-400 font-mono">{p.receipt_number}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{formatDate(p.payment_date)} · {PAYMENT_METHODS_BILLING.find(m => m.value === p.method)?.label}</p>
                    </div>
                    <span className="font-semibold text-emerald-600 font-mono text-sm">{formatMoney(p.amount)}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        title="View / send receipt"
                        onClick={() => setViewingReceipt(p)}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-indigo-500 transition-colors">
                        <Receipt size={13} />
                      </button>
                      <button onClick={() => removePayment(p)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardBody>
      </Card>

      {/* ── Extra charges ── */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <AlertCircle size={14} /> Extra Charges
            </p>
            <button type="button" onClick={() => setShowChargeForm(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors">
              <Plus size={14} /> Add charge
            </button>
          </div>

          {showChargeForm && (
            <form onSubmit={saveCharge} className="mb-4 p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                  <Input value={chargeForm.description} onChange={(e) => setChargeForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. High Court filing fee" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Amount (GHS)</label>
                  <Input type="number" min="0.01" step="0.01" value={chargeForm.amount} onChange={(e) => setChargeForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
                  <Input type="date" value={chargeForm.charge_date} onChange={(e) => setChargeForm(f => ({ ...f, charge_date: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                  <Select value={chargeForm.charge_type} onChange={(e) => setChargeForm(f => ({ ...f, charge_type: e.target.value }))} options={CHARGE_TYPES_BILLING} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                  <Input value={chargeForm.notes} onChange={(e) => setChargeForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => setShowChargeForm(false)}>Cancel</Button>
                <Button type="submit" variant="secondary" disabled={savingCharge}>{savingCharge ? 'Saving…' : 'Add charge'}</Button>
              </div>
            </form>
          )}

          {loading ? <p className="text-sm text-slate-400 py-2">Loading…</p>
            : charges.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">No extra charges yet</p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {charges.map((c) => (
                  <div key={c.id} className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 group ${c.status === 'paid' ? 'opacity-60' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${c.status === 'paid' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                      <FileText size={14} className={c.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{c.description}</p>
                      <p className="text-xs text-slate-400">{formatDate(c.charge_date)} · {CHARGE_TYPES_BILLING.find(t => t.value === c.charge_type)?.label}</p>
                    </div>
                    <span className="font-semibold text-amber-600 font-mono text-sm">+{formatMoney(c.amount)}</span>
                    {c.status === 'paid'
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">Paid</span>
                      : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">Unpaid</span>
                    }
                    <button onClick={() => openEditCharge(c)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-500 transition-all" title="Edit charge"><Pencil size={13} /></button>
                    <button onClick={() => removeCharge(c)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all" title="Delete charge"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
        </CardBody>
      </Card>

      {/* ── Invoices ── */}
      <Card>
        <CardBody className="p-0">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <ReceiptText size={14} /> Invoices
            </p>
            <button
              onClick={openAddInvoice}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors">
              <Plus size={14} /> Add invoice
            </button>
          </div>
          {invoices.length === 0 ? (
            <div className="p-6">
              <EmptyState icon={ReceiptText} title="No invoices" description="Click 'Add invoice' above to create one for this matter." />
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {invoices.map((inv) => (
                  <li key={inv.id} className={`px-4 py-3 flex items-start justify-between gap-2 ${inv.status === 'paid' ? 'opacity-60' : ''}`}>
                    <div>
                      <p className="font-semibold text-sm">{inv.invoice_number || '—'}</p>
                      <p className="text-xs text-slate-500">Issued {formatDate(inv.issue_date)} · Due {formatDate(inv.due_date)}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge tone={INVOICE_TONE[inv.status] || 'slate'}>{inv.status}</Badge>
                        {inv.status === 'draft' && (
                          <button
                            onClick={() => changeInvoiceStatus(inv, 'sent')}
                            className="text-xs px-2 py-0.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition">
                            Send to client
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Total: <span className="font-mono font-medium">{formatMoney(inv.total || 0)}</span></p>
                      <p className="text-xs font-mono font-bold"><span className={inv.balance === 0 ? 'text-emerald-600' : 'text-red-600'}>{formatMoney(inv.balance || 0)}</span></p>
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <button onClick={() => downloadInvoice(inv)} className="text-slate-400 hover:text-indigo-500"><Download size={13} /></button>
                        <button onClick={() => setSharingInvoice(inv)} className="text-slate-400 hover:text-indigo-500"><Share2 size={13} /></button>
                        <button onClick={() => removeInvoice(inv)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-6 py-2 font-medium">Invoice #</th>
                  <th className="text-left px-6 py-2 font-medium">Issue date</th>
                  <th className="text-left px-6 py-2 font-medium">Due date</th>
                  <th className="text-right px-6 py-2 font-medium">Total</th>
                  <th className="text-right px-6 py-2 font-medium">Balance</th>
                  <th className="text-left px-6 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {invoices.map((inv) => (
                  <tr key={inv.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800 group ${inv.status === 'paid' ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-3 font-semibold text-slate-800 dark:text-slate-200">{inv.invoice_number || '—'}</td>
                    <td className="px-6 py-3 text-slate-500">{formatDate(inv.issue_date)}</td>
                    <td className="px-6 py-3 text-slate-500">{formatDate(inv.due_date)}</td>
                    <td className="px-6 py-3 text-right font-mono font-medium">{formatMoney(inv.total || 0)}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold">
                      <span className={inv.balance === 0 ? 'text-emerald-600' : 'text-red-600'}>{formatMoney(inv.balance || 0)}</span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Badge tone={INVOICE_TONE[inv.status] || 'slate'}>{inv.status}</Badge>
                        {inv.status === 'draft' && (
                          <button
                            onClick={() => changeInvoiceStatus(inv, 'sent')}
                            className="text-xs px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium transition whitespace-nowrap">
                            Send to client
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => downloadInvoice(inv)}
                          className="text-slate-400 hover:text-indigo-500"
                          title="Download PDF">
                          <Download size={14} />
                        </button>
                        <button
                          onClick={() => setSharingInvoice(inv)}
                          className="text-slate-400 hover:text-indigo-500"
                          title="Share invoice">
                          <Share2 size={14} />
                        </button>
                        <button
                          onClick={() => removeInvoice(inv)}
                          className="text-slate-300 hover:text-red-500"
                          title="Delete invoice">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDeleteModal
        open={!!deletingPayment}
        onClose={() => setDeletingPayment(null)}
        onConfirm={async () => {
          if (!deletingPayment) return;
          await deletePayment(deletingPayment.id);
          setPayments((prev) => prev.filter((x) => x.id !== deletingPayment.id));
          setDeletingPayment(null);
        }}
        title="Delete payment"
        description={deletingPayment ? `Delete this payment of ${formatMoney(deletingPayment.amount)}? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />
      <ConfirmDeleteModal
        open={!!deletingCharge}
        onClose={() => setDeletingCharge(null)}
        onConfirm={async () => {
          if (!deletingCharge) return;
          await deleteCaseCharge(deletingCharge.id);
          setCharges((prev) => prev.filter((x) => x.id !== deletingCharge.id));
          setDeletingCharge(null);
        }}
        title="Delete charge"
        description={deletingCharge ? `Delete the charge for ${deletingCharge.description}? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />
      <ConfirmDeleteModal
        open={!!deletingInvoice}
        onClose={() => setDeletingInvoice(null)}
        onConfirm={async () => {
          if (!deletingInvoice) return;
          const { error } = await deleteInvoice(deletingInvoice.id);
          if (error) { toast.error(error.message); setDeletingInvoice(null); return; }
          setLocalInvoices((prev) => prev.filter((i) => i.id !== deletingInvoice.id));
          setDeletingInvoice(null);
        }}
        title="Delete invoice"
        description={deletingInvoice ? `Delete invoice ${deletingInvoice.invoice_number}? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />

      {/* ── Share Invoice Modal ── */}
      <Modal open={!!sharingInvoice} title="Share Invoice" onClose={() => { setSharingInvoice(null); setLinkCopied(false); }}>
        {sharingInvoice && (
          <div className="space-y-5">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Share <span className="font-semibold text-slate-700 dark:text-slate-200">{sharingInvoice.invoice_number}</span> with your client.
              The link lets them view the invoice without logging in.
            </p>

            {/* Link box */}
            <div className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <span className="flex-1 text-xs font-mono text-slate-600 dark:text-slate-300 truncate">
                {invoiceShareUrl(sharingInvoice)}
              </span>
              <button
                onClick={() => copyInvoiceLink(sharingInvoice)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
                  linkCopied
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}>
                {linkCopied ? <><Check size={12} /> Copied!</> : <><Copy size={12} /> Copy link</>}
              </button>
            </div>

            {/* Download + WhatsApp buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => downloadInvoice(sharingInvoice)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm transition-colors">
                <Download size={15} />
                Download PDF
              </button>
              <button
                onClick={() => openWhatsApp(sharingInvoice)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium text-sm transition-colors">
                <MessageCircle size={15} />
                Send via WhatsApp
              </button>
            </div>

            {!matter?.client?.phone && (
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                ⚠ No phone number on file for this client — WhatsApp will open without a pre-filled recipient.
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* ── Edit Charge Modal ── */}
      <Modal open={!!editingCharge} title="Edit Charge" onClose={() => setEditingCharge(null)}>
        {editingCharge && (
          <form onSubmit={saveEditCharge} className="space-y-4">
            <div>
              <Label>Description</Label>
              <Input value={editChargeForm.description} onChange={(e) => setEditChargeForm(f => ({ ...f, description: e.target.value }))} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (GHS)</Label>
                <Input type="number" min="0.01" step="0.01" value={editChargeForm.amount} onChange={(e) => setEditChargeForm(f => ({ ...f, amount: e.target.value }))} required />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={editChargeForm.charge_date} onChange={(e) => setEditChargeForm(f => ({ ...f, charge_date: e.target.value }))} required />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={editChargeForm.charge_type} onChange={(e) => setEditChargeForm(f => ({ ...f, charge_type: e.target.value }))} options={CHARGE_TYPES_BILLING} />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={editChargeForm.notes} onChange={(e) => setEditChargeForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setEditingCharge(null)}>Cancel</Button>
              <Button type="submit" disabled={savingEditCharge}>{savingEditCharge ? 'Saving…' : 'Save changes'}</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Add Invoice Modal ── */}
      <Modal open={showAddInvoice} title="New Invoice" onClose={() => setShowAddInvoice(false)} size="xl">
        <form onSubmit={submitNewInvoice} className="space-y-5">
          {/* Basic fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Invoice #</Label>
              <Input value={invForm.invoice_number} onChange={(e) => setInvForm({ ...invForm, invoice_number: e.target.value })} required />
            </div>
            <div>
              <Label>Issue Date</Label>
              <Input type="date" value={invForm.issue_date} onChange={(e) => setInvForm({ ...invForm, issue_date: e.target.value })} required />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">Line Items</Label>
              <button type="button" onClick={() => setInvLines((p) => [...p, blankLine()])}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors">
                <Plus size={14} /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {/* Header row */}
              <div className="grid grid-cols-12 gap-2 px-1 mb-1">
                <span className="col-span-2 text-xs text-slate-400 uppercase tracking-wide">Type</span>
                <span className="col-span-4 text-xs text-slate-400 uppercase tracking-wide">Description</span>
                <span className="col-span-2 text-xs text-slate-400 uppercase tracking-wide text-right">Qty</span>
                <span className="col-span-2 text-xs text-slate-400 uppercase tracking-wide text-right">Rate (GHS)</span>
                <span className="col-span-2 text-xs text-slate-400 uppercase tracking-wide text-right">Amount</span>
              </div>
              {invLines.map((line) => (
                <div key={line._id} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-2">
                    <Select
                      options={LINE_TYPES}
                      value={line.item_type}
                      onChange={(e) => updateLine(line._id, 'item_type', e.target.value)}
                    />
                  </div>
                  <div className="col-span-4">
                    <Input placeholder="Description" value={line.description} onChange={(e) => updateLine(line._id, 'description', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" placeholder="1" min="0" step="0.01" value={line.quantity} onChange={(e) => updateLine(line._id, 'quantity', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" placeholder="0.00" min="0" step="0.01" value={line.rate} onChange={(e) => updateLine(line._id, 'rate', e.target.value)} />
                  </div>
                  <div className="col-span-2 flex items-center justify-between gap-1">
                    <span className="text-xs font-mono text-slate-600 dark:text-slate-400">{formatMoney(parseFloat(line.amount) || 0)}</span>
                    {invLines.length > 1 && (
                      <button type="button" onClick={() => setInvLines((p) => p.filter((l) => l._id !== line._id))}
                        className="text-slate-300 hover:text-red-500 flex-shrink-0"><X size={12} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals row */}
          <div className="flex flex-col sm:flex-row gap-6 border-t border-slate-100 dark:border-slate-800 pt-4">
            <div className="flex-1">
              <GhanaTaxPicker
                selectedLevies={invSelectedLevies}
                customRate={invCustomTaxRate}
                subtotal={invSubtotal}
                onChange={(levies, combined) => {
                  setInvSelectedLevies(levies);
                  setInvForm({ ...invForm, tax_rate: combined + (invCustomTaxRate || 0) });
                }}
                onCustomRate={(v) => {
                  setInvCustomTaxRate(v);
                  const levyTotal = GHANA_TAX_LEVIES.filter((l) => invSelectedLevies.includes(l.value)).reduce((s, l) => s + l.rate, 0);
                  setInvForm({ ...invForm, tax_rate: levyTotal + v });
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-right self-end text-sm min-w-[220px]">
              <span className="text-slate-500">Subtotal</span><span className="font-mono font-medium">{formatMoney(invSubtotal)}</span>
              {invTaxAmt > 0 && (<><span className="text-slate-500">Tax ({parseFloat(invForm.tax_rate) || 0}%)</span><span className="font-mono">{formatMoney(invTaxAmt)}</span></>)}
              <span className="text-slate-500">Discount (GHS)</span>
              <input type="number" min="0" step="0.01" value={invForm.discount_amount}
                onChange={(e) => setInvForm({ ...invForm, discount_amount: e.target.value })}
                className="w-20 text-right border border-slate-200 dark:border-slate-700 rounded px-2 py-0.5 text-sm bg-white dark:bg-slate-900 font-mono ml-auto" />
              <span className="font-semibold text-slate-800 dark:text-slate-200 border-t border-slate-200 dark:border-slate-700 pt-1">Total</span>
              <span className="font-semibold font-mono text-slate-800 dark:text-slate-200 border-t border-slate-200 dark:border-slate-700 pt-1">{formatMoney(invTotal)}</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={invForm.notes} onChange={(e) => setInvForm({ ...invForm, notes: e.target.value })} placeholder="Payment terms, instructions…" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowAddInvoice(false)}>Cancel</Button>
            <Button type="submit" disabled={savingInvoice}>{savingInvoice ? 'Saving…' : 'Create Invoice'}</Button>
          </div>
        </form>
      </Modal>

      {/* ── Past receipt re-view modal ── */}
      <Modal open={!!viewingReceipt} title="Receipt" onClose={() => setViewingReceipt(null)}>
        {viewingReceipt && (() => {
          const rec = buildReceiptFromPayment(viewingReceipt);
          const clientPhone = (rec.client?.phone || '').replace(/\D/g, '');
          return (
            <div className="space-y-5">
              <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white px-6 py-5 text-center">
                <p className="text-xs uppercase tracking-widest opacity-75 mb-1">Amount Received</p>
                <p className="text-3xl font-extrabold tracking-tight">{formatMoney(rec.amount)}</p>
                <p className="text-indigo-200 text-sm mt-1">{rec.receiptNumber}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Received from', value: [rec.client?.first_name, rec.client?.last_name].filter(Boolean).join(' ') || rec.client?.organization || '—' },
                  { label: 'Date', value: rec.payment_date ? new Date(rec.payment_date).toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
                  { label: 'Method', value: RECEIPT_METHOD_LABELS[rec.method] || rec.method },
                  { label: 'Matter', value: rec.matterTitle || '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
                    <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">{value}</p>
                  </div>
                ))}
              </div>
              {viewingReceipt.payment_label && (
                <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-4 py-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">Reference</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">{viewingReceipt.payment_label}</p>
                </div>
              )}
              {viewingReceipt.notes && (
                <div className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-4 py-3 text-sm">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">Notes</p>
                  <p className="text-slate-600 dark:text-slate-300 whitespace-pre-line">{viewingReceipt.notes}</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => printReceipt(rec)}
                  className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs transition-colors">
                  <Download size={16} />Download PDF
                </button>
                <button onClick={() => {
                  const text = encodeURIComponent(`Hi${rec.client?.first_name ? ' ' + rec.client.first_name : ''},\n\nYour payment receipt:\n\nReceipt: ${rec.receiptNumber}\nAmount: GH₵${Number(rec.amount).toLocaleString('en-GH', { minimumFractionDigits: 2 })}\nDate: ${rec.payment_date}\nMethod: ${RECEIPT_METHOD_LABELS[rec.method] || rec.method}\n\nThank you.\n\n${rec.firm?.name || ''}`);
                  window.open(`https://wa.me/${clientPhone}?text=${text}`, '_blank');
                }} className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium text-xs transition-colors">
                  <MessageCircle size={16} />WhatsApp
                </button>
                <button onClick={() => {
                  const subject = encodeURIComponent(`Payment Receipt – ${rec.receiptNumber}`);
                  const body = encodeURIComponent(`Dear ${rec.client?.first_name || 'Client'},\n\nYour payment receipt:\n\nReceipt: ${rec.receiptNumber}\nAmount: GH₵${Number(rec.amount).toLocaleString('en-GH', { minimumFractionDigits: 2 })}\nDate: ${rec.payment_date}\nMethod: ${RECEIPT_METHOD_LABELS[rec.method] || rec.method}\nMatter: ${rec.matterTitle}\n\nThank you.\n\n${rec.firm?.name || ''}`);
                  window.open(`mailto:${rec.client?.email || ''}?subject=${subject}&body=${body}`, '_blank');
                }} className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-medium text-xs transition-colors">
                  <Mail size={16} />Send Email
                </button>
              </div>
              {!rec.client?.phone && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">⚠ No phone number on file — WhatsApp will open without a pre-filled recipient.</p>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* ── Payment Receipt Modal ── */}
      <Modal open={showReceiptModal} title="Payment Receipt" onClose={() => setShowReceiptModal(false)}>
        {receiptData && (
          <div className="space-y-5">
            {/* Amount hero */}
            <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white px-6 py-5 text-center">
              <p className="text-xs uppercase tracking-widest opacity-75 mb-1">Amount Received</p>
              <p className="text-3xl font-extrabold tracking-tight">{formatMoney(receiptData.amount)}</p>
              <p className="text-indigo-200 text-sm mt-1">{receiptData.receiptNumber}</p>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                { label: 'Received from', value: [receiptData.client?.first_name, receiptData.client?.last_name].filter(Boolean).join(' ') || receiptData.client?.organization || '—' },
                { label: 'Date', value: receiptData.payment_date ? new Date(receiptData.payment_date).toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
                { label: 'Method', value: { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', mobile_money: 'Mobile Money', card: 'Card', other: 'Other' }[receiptData.method] || receiptData.method },
                { label: 'Matter', value: receiptData.matterTitle || '—' },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-4 py-3">
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">{label}</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">{value}</p>
                </div>
              ))}
            </div>

            {/* Applied to */}
            {(receiptData.linkedInvoices?.length > 0 || receiptData.linkedCharges?.length > 0) && (
              <div className="rounded-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
                <p className="text-xs uppercase tracking-wide text-slate-400 px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">Applied to</p>
                <div className="divide-y divide-slate-100 dark:divide-slate-700">
                  {receiptData.linkedInvoices.map((inv) => (
                    <div key={inv.invoice_number} className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-slate-600 dark:text-slate-300">Invoice {inv.invoice_number}</span>
                      <span className="font-semibold font-mono text-slate-800 dark:text-slate-100">{formatMoney(inv.total)}</span>
                    </div>
                  ))}
                  {receiptData.linkedCharges.map((c, i) => (
                    <div key={i} className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-slate-600 dark:text-slate-300">{c.description}</span>
                      <span className="font-semibold font-mono text-slate-800 dark:text-slate-100">{formatMoney(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => printReceipt(receiptData)}
                className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs transition-colors">
                <Download size={16} />
                Download PDF
              </button>
              <button
                onClick={() => {
                  const clientPhone = (receiptData.client?.phone || '').replace(/\D/g, '');
                  const text = encodeURIComponent(
                    `Hi${receiptData.client?.first_name ? ' ' + receiptData.client.first_name : ''},\n\nPlease find your payment receipt below:\n\nReceipt: ${receiptData.receiptNumber}\nAmount: GH₵${Number(receiptData.amount).toLocaleString('en-GH', { minimumFractionDigits: 2 })}\nDate: ${receiptData.payment_date}\nMethod: ${receiptData.method}\n\nThank you for your payment.\n\n${receiptData.firm?.name || ''}`
                  );
                  window.open(`https://wa.me/${clientPhone}?text=${text}`, '_blank');
                }}
                className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium text-xs transition-colors">
                <MessageCircle size={16} />
                WhatsApp
              </button>
              <button
                onClick={() => {
                  const clientEmail = receiptData.client?.email || '';
                  const subject = encodeURIComponent(`Payment Receipt – ${receiptData.receiptNumber}`);
                  const body = encodeURIComponent(
                    `Dear ${receiptData.client?.first_name || 'Client'},\n\nPlease find your payment receipt details below:\n\nReceipt Number: ${receiptData.receiptNumber}\nAmount Paid: GH₵${Number(receiptData.amount).toLocaleString('en-GH', { minimumFractionDigits: 2 })}\nPayment Date: ${receiptData.payment_date}\nPayment Method: ${receiptData.method}\nMatter: ${receiptData.matterTitle}\n\nThank you for your payment.\n\nKind regards,\n${receiptData.firm?.name || ''}`
                  );
                  window.open(`mailto:${clientEmail}?subject=${subject}&body=${body}`, '_blank');
                }}
                className="flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white font-medium text-xs transition-colors">
                <Mail size={16} />
                Send Email
              </button>
            </div>
            {!receiptData.client?.phone && (
              <p className="text-xs text-amber-600 dark:text-amber-400 text-center">⚠ No phone number on file — WhatsApp will open without a pre-filled recipient.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Notes ───────────────────────────────────────────────────────────────────
const EMPTY_NOTE = { title: '', content: '', note_date: new Date().toISOString().slice(0, 10) };

const NotesTab = ({ notes, setNotes, firmId, matterId, userId }) => {
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null); // note id being edited
  const [form, setForm]           = useState(EMPTY_NOTE);
  const [saving, setSaving]       = useState(false);
  const [expanded, setExpanded]   = useState(null); // note id expanded
  const [deletingNote, setDeletingNote] = useState(null);

  const openAdd = () => { setForm(EMPTY_NOTE); setEditing(null); setShowForm(true); };
  const openEdit = (note) => {
    setForm({ title: note.title, content: note.content, note_date: note.note_date });
    setEditing(note.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.content.trim()) return;
    setSaving(true);
    if (editing) {
      const { data } = await updateMatterNote(editing, form);
      if (data) setNotes((prev) => prev.map((n) => n.id === editing ? data : n));
    } else {
      const { data } = await createMatterNote(firmId, { matter_id: matterId, user_id: userId, ...form });
      if (data) setNotes((prev) => [data, ...prev]);
    }
    setSaving(false);
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (id) => {
    setDeletingNote(id);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white">Case notes</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Private to your firm — not visible to clients</p>
        </div>
        <Button icon={StickyNote} onClick={openAdd}>Add note</Button>
      </div>

      {/* Notes list */}
      {notes.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={StickyNote}
              title="No notes yet"
              description="Add your first note — hearing summaries, strategy, key facts, internal memos."
              action={<Button onClick={openAdd}>Add first note</Button>}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const isOpen = expanded === note.id;
            const author = note.user ? `${note.user.first_name || ''} ${note.user.last_name || ''}`.trim() : 'Unknown';
            return (
              <Card key={note.id} className="overflow-hidden">
                {/* Note header — always visible */}
                <button
                  onClick={() => setExpanded(isOpen ? null : note.id)}
                  className="w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <StickyNote size={15} className="text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                      {note.title || 'Untitled note'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {formatDate(note.note_date)} · {author}
                    </p>
                    {!isOpen && note.content && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 whitespace-pre-wrap">
                        {note.content}
                      </p>
                    )}
                  </div>
                  <span className={`text-slate-400 transition-transform flex-shrink-0 mt-1 ${isOpen ? 'rotate-180' : ''}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                  </span>
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div className="border-t border-slate-100 dark:border-slate-800">
                    <div className="px-5 py-4">
                      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                        {note.content || <span className="text-slate-400 italic">No content.</span>}
                      </p>
                    </div>
                    <div className="flex items-center justify-end gap-2 px-5 pb-4">
                      <button
                        onClick={() => openEdit(note)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(note.id)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit note' : 'Add note'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.content.trim()}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add note'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Title <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Hearing on 21 April — motion denied"
            />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={form.note_date}
              onChange={(e) => setForm({ ...form, note_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Notes *</Label>
            <Textarea
              rows={8}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write your notes here — what happened in court, key arguments, next steps, strategy…"
            />
          </div>
        </div>
      </Modal>
      <ConfirmDeleteModal
        open={!!deletingNote}
        onClose={() => setDeletingNote(null)}
        onConfirm={async () => {
          if (!deletingNote) return;
          await deleteMatterNote(deletingNote);
          setNotes((prev) => prev.filter((n) => n.id !== deletingNote));
          setDeletingNote(null);
        }}
        title="Delete note"
        description="Delete this note? This cannot be undone."
        confirmWord="DELETE"
      />
    </div>
  );
};

// ─── Deadlines Tab ───────────────────────────────────────────────────────────
const DEADLINE_STATUSES = [
  { value: 'open',      label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'waived',    label: 'Waived' },
];

const EMPTY_DL = { title: '', due_date: '', status: 'open', priority: 'high', rule_template: '', trigger_date: '', notes: '' };

const DL_PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
];

function addCalendarDaysDL(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addBusinessDaysDL(date, days) {
  let d = new Date(date);
  let added = 0;
  const step = days < 0 ? -1 : 1;
  while (added < Math.abs(days)) {
    d.setDate(d.getDate() + step);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d;
}

function calcDueDateDL(triggerDate, offsetDays, businessDays) {
  if (!triggerDate) return null;
  const base = new Date(triggerDate + 'T12:00:00');
  const result = businessDays
    ? addBusinessDaysDL(base, offsetDays)
    : addCalendarDaysDL(base, offsetDays);
  return result.toISOString().slice(0, 10);
}

function deadlineTone(d) {
  if (d.status === 'completed') return 'green';
  if (d.status === 'waived')    return 'slate';
  if (!d.due_date)              return 'amber';
  const daysLeft = Math.ceil((new Date(d.due_date) - Date.now()) / 86400000);
  if (daysLeft < 0)  return 'red';
  if (daysLeft <= 7) return 'amber';
  return 'blue';
}

function deadlineUrgencyLabel(d) {
  if (d.status !== 'open' || !d.due_date) return null;
  const daysLeft = Math.ceil((new Date(d.due_date) - Date.now()) / 86400000);
  if (daysLeft < 0)  return `${Math.abs(daysLeft)}d overdue`;
  if (daysLeft === 0) return 'Due today';
  if (daysLeft <= 7) return `${daysLeft}d left`;
  return null;
}

const DeadlinesTab = ({ deadlines, setDeadlines, firmId, matterId }) => {
  const [showModal,       setShowModal]       = useState(false);
  const [editTarget,      setEditTarget]      = useState(null);
  const [form,            setForm]            = useState(EMPTY_DL);
  const [saving,          setSaving]          = useState(false);
  const [toggling,        setToggling]        = useState({});
  const [dlError,         setDlError]         = useState('');
  const [deletingDeadline, setDeletingDeadline] = useState(null);

  const closeModal = () => { setShowModal(false); setDlError(''); };

  const openAdd = () => { setForm(EMPTY_DL); setEditTarget(null); setDlError(''); setShowModal(true); };
  const openEdit = (d) => {
    setForm({
      title:        d.title        || '',
      due_date:     d.due_date     || '',
      status:       d.status       || 'open',
      priority:     d.priority     || 'high',
      rule_template: d.rule_template || '',
      trigger_date: d.trigger_date || '',
      notes:        d.notes        || '',
    });
    setEditTarget(d);
    setDlError('');
    setShowModal(true);
  };

  // Live update helper — recalculates due date when rule or trigger changes
  const setField = (key, value) => {
    setForm((f) => {
      const updated = { ...f, [key]: value };
      if ((key === 'trigger_date' || key === 'rule_template') && updated.rule_template && updated.trigger_date) {
        const tpl = DEADLINE_RULE_TEMPLATES.find((t) => t.value === updated.rule_template);
        if (tpl) {
          updated.due_date = calcDueDateDL(updated.trigger_date, tpl.offsetDays, tpl.businessDays) || updated.due_date;
          if (!updated.title) updated.title = tpl.label;
        }
      }
      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.due_date) {
      setDlError('Title and due date are required.');
      return;
    }
    setSaving(true);
    setDlError('');
    if (editTarget) {
      const { data, error } = await updateDeadline(editTarget.id, form);
      if (error) { setDlError(error.message || 'Failed to save deadline.'); setSaving(false); return; }
      if (data) setDeadlines((prev) => prev.map((d) => d.id === editTarget.id ? { ...d, ...data } : d));
    } else {
      const { data, error } = await createDeadline(firmId, { matter_id: matterId, ...form });
      if (error) { setDlError(error.message || 'Failed to create deadline.'); setSaving(false); return; }
      if (data) setDeadlines((prev) => [data, ...prev]);
    }
    setSaving(false);
    closeModal();
  };

  const handleDelete = async (d) => {
    setDeletingDeadline(d);
  };

  const toggleStatus = async (d) => {
    const next = d.status === 'completed' ? 'open' : 'completed';
    setToggling((t) => ({ ...t, [d.id]: true }));
    const { data, error } = await updateDeadline(d.id, { status: next });
    if (!error && data) setDeadlines((prev) => prev.map((x) => x.id === d.id ? { ...x, status: next } : x));
    setToggling((t) => ({ ...t, [d.id]: false }));
  };

  const sorted = [...deadlines].sort((a, b) => {
    // open/overdue first, then by date
    const aOpen = a.status === 'open' ? 0 : 1;
    const bOpen = b.status === 'open' ? 0 : 1;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return new Date(a.due_date || 0) - new Date(b.due_date || 0);
  });

  const openCount = deadlines.filter((d) => d.status === 'open').length;
  const overdueCount = deadlines.filter((d) => d.status === 'open' && d.due_date && new Date(d.due_date) < Date.now()).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white">Deadlines</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {openCount} open{overdueCount > 0 && <span className="text-red-600 font-medium"> · {overdueCount} overdue</span>}
          </p>
        </div>
        <Button icon={Plus} onClick={openAdd}>Add deadline</Button>
      </div>

      {sorted.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={CalendarIcon}
              title="No deadlines yet"
              description="Add individual deadlines or generate a deadline chain from the Calendar page."
              action={<Button icon={Plus} onClick={openAdd}>Add first deadline</Button>}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-2">
          {sorted.map((d) => {
            const tone = deadlineTone(d);
            const urgency = deadlineUrgencyLabel(d);
            const done = d.status === 'completed';
            return (
              <div
                key={d.id}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 flex items-start gap-3 hover:shadow-sm transition"
              >
                {/* Check button */}
                <button
                  onClick={() => toggleStatus(d)}
                  disabled={toggling[d.id]}
                  className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    done
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400'
                  }`}
                  title={done ? 'Mark open' : 'Mark completed'}
                >
                  {done && <CheckCircle2 size={12} />}
                </button>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${done ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
                      {d.title}
                    </p>
                    <Badge tone={tone}>{d.status.replace('_', ' ')}</Badge>
                    {urgency && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tone === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                        {urgency}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {d.due_date ? formatDate(d.due_date) : 'No date set'}
                    {d.rule_template && ` · ${d.rule_template}`}
                  </p>
                  {d.notes && <p className="text-xs text-slate-400 mt-1 italic">{d.notes}</p>}
                </div>

                {/* Hover actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => openEdit(d)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-indigo-600"
                    title="Edit"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(d)}
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editTarget ? 'Edit deadline' : 'Add deadline'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.title.trim() || !form.due_date}>
              {saving ? 'Saving…' : editTarget ? 'Save changes' : 'Add deadline'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">

          {/* ── Auto-calculate section ── */}
          <div className="rounded-lg border border-indigo-100 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 p-4 space-y-3">
            <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
              Auto-calculate from rule
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Deadline rule</Label>
                <Select
                  placeholder="Select rule…"
                  options={DEADLINE_RULE_TEMPLATES.map((t) => ({ value: t.value, label: t.label }))}
                  value={form.rule_template}
                  onChange={(e) => setField('rule_template', e.target.value)}
                />
              </div>
              <div>
                <Label>Trigger date</Label>
                <Input
                  type="date"
                  value={form.trigger_date}
                  onChange={(e) => setField('trigger_date', e.target.value)}
                />
              </div>
            </div>
            {form.rule_template && form.trigger_date && (() => {
              const tpl = DEADLINE_RULE_TEMPLATES.find((t) => t.value === form.rule_template);
              return tpl ? (
                <p className="text-xs text-indigo-600 dark:text-indigo-300">
                  <strong>{tpl.label}</strong>
                  {' '}— {tpl.offsetDays > 0 ? '+' : ''}{tpl.offsetDays}{' '}
                  {tpl.businessDays ? 'business days' : 'calendar days'}
                  {' '}from trigger →{' '}
                  <strong>{form.due_date ? formatDate(form.due_date) : '—'}</strong>
                  {' '}({tpl.jurisdiction})
                </p>
              ) : null;
            })()}
          </div>

          {/* ── Manual fields ── */}
          <div>
            <Label required>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              placeholder="e.g. File motion to dismiss"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label required>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setField('due_date', e.target.value)} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
                options={DL_PRIORITY_OPTIONS}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                options={DEADLINE_STATUSES}
              />
            </div>
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Any additional context…"
            />
          </div>

          {dlError && (
            <p className="text-sm text-red-600 dark:text-red-400">{dlError}</p>
          )}
        </div>
      </Modal>
      <ConfirmDeleteModal
        open={!!deletingDeadline}
        onClose={() => setDeletingDeadline(null)}
        onConfirm={async () => {
          if (!deletingDeadline) return;
          await deleteDeadline(deletingDeadline.id);
          setDeadlines((prev) => prev.filter((x) => x.id !== deletingDeadline.id));
          setDeletingDeadline(null);
        }}
        title="Delete deadline"
        description={deletingDeadline ? `Delete deadline "${deletingDeadline.title}"? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />
    </div>
  );
};

const TimeEntriesList = ({ entries, setEntries, onLogTime, firmId }) => {
  const [deletingTimeEntry, setDeletingTimeEntry] = useState(null);
  const totalMins = entries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
  const totalFees = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  const handleDelete = async (entry) => {
    setDeletingTimeEntry(entry);
  };

  return (
    <>
    <Card>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-6 text-sm text-slate-500">
          {entries.length > 0 && (
            <>
              <span><span className="font-semibold text-slate-800 dark:text-slate-200">{formatDuration(totalMins)}</span> total</span>
              <span><span className="font-semibold text-emerald-600">{formatMoney(totalFees)}</span> billed</span>
              <span><span className="font-semibold text-slate-800 dark:text-slate-200">{entries.length}</span> entr{entries.length === 1 ? 'y' : 'ies'}</span>
            </>
          )}
        </div>
        <Button icon={Plus} size="sm" onClick={onLogTime}>Log time</Button>
      </div>
      <CardBody className="p-0">
        {!entries.length ? (
          <EmptyState icon={Clock} title="No time logged yet"
            description="Click 'Log time' to record time against this matter."
            action={<Button icon={Plus} onClick={onLogTime}>Log time</Button>} />
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {entries.map((e) => (
                <li key={e.id} className="px-4 py-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm">{e.description || <span className="text-slate-400 italic">No description</span>}</p>
                    <p className="text-xs text-slate-400">{formatDate(e.entry_date)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${e.billable ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500'}`}>
                      {e.billable ? 'Billable' : 'Non-billable'}
                    </span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono text-sm">{formatDuration(e.duration_minutes)}</p>
                    <p className="font-mono font-semibold text-emerald-600 text-sm">{e.billable ? formatMoney(e.amount || 0) : '—'}</p>
                    <button onClick={() => handleDelete(e)} className="text-slate-300 hover:text-red-500 mt-1"><Trash2 size={13} /></button>
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-6 py-2">Date</th>
                  <th className="text-left px-6 py-2">Description</th>
                  <th className="text-left px-6 py-2">Billable</th>
                  <th className="text-right px-6 py-2">Duration</th>
                  <th className="text-right px-6 py-2">Amount</th>
                  <th className="px-6 py-2 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 group">
                    <td className="px-6 py-3 text-slate-500">{formatDate(e.entry_date)}</td>
                    <td className="px-6 py-3">{e.description || <span className="text-slate-400 italic">No description</span>}</td>
                    <td className="px-6 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${e.billable ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500'}`}>
                        {e.billable ? 'Billable' : 'Non-billable'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-mono">{formatDuration(e.duration_minutes)}</td>
                    <td className="px-6 py-3 text-right font-mono font-semibold text-emerald-600">{e.billable ? formatMoney(e.amount || 0) : '—'}</td>
                    <td className="px-6 py-3 text-right">
                      <button onClick={() => handleDelete(e)} className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-red-500" title="Delete entry"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </CardBody>
    </Card>
    <ConfirmDeleteModal
      open={!!deletingTimeEntry}
      onClose={() => setDeletingTimeEntry(null)}
      onConfirm={async () => {
        if (!deletingTimeEntry) return;
        setEntries((prev) => prev.filter((e) => e.id !== deletingTimeEntry.id));
        await deleteTimeEntry(deletingTimeEntry.id);
        setDeletingTimeEntry(null);
      }}
      title="Delete time entry"
      description={deletingTimeEntry ? `Delete this time entry (${formatDuration(deletingTimeEntry.duration_minutes)})? This cannot be undone.` : ''}
      confirmWord="DELETE"
    />
    </>
  );
};

function fileIcon(mime) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return FileImage;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return FileSpreadsheet;
  return FileText;
}

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DOC_CATEGORIES = [
  { value: 'pleading',       label: 'Pleading' },
  { value: 'contract',       label: 'Contract' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'evidence',       label: 'Evidence' },
  { value: 'court_order',    label: 'Court Order' },
  { value: 'invoice',        label: 'Invoice' },
  { value: 'retainer',       label: 'Retainer' },
  { value: 'other',          label: 'Other' },
];

const DocumentsList = ({ documents, setDocuments, firmId, matterId, matter }) => {
  const [editDoc,      setEditDoc]      = useState(null);
  const [editForm,     setEditForm]     = useState({ name: '', description: '', category: 'other' });
  const [savingEdit,   setSavingEdit]   = useState(false);
  const [uploading,    setUploading]    = useState(false);
  // `name` lets the user override the source filename (which often
  // comes from the OS as `IMG_4271.HEIC`, `Dockerfile` with no
  // extension, `scan_001.pdf`, etc.). Defaulting to the source filename
  // keeps current behaviour for users who don't care; the field is
  // pre-populated when the file picker fires.
  const [uploadForm,   setUploadForm]   = useState({ name: '', category: 'other', description: '' });
  const [showUpload,   setShowUpload]   = useState(false);
  const [deletingDoc,  setDeletingDoc]  = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // Portal document requests
  const [requests,        setRequests]        = useState([]);
  const [reqLoading,      setReqLoading]      = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqForm,         setReqForm]         = useState({ title: '', description: '', dueAt: '' });
  const [savingReq,       setSavingReq]       = useState(false);
  const [reviewing,       setReviewing]       = useState(null);   // request being reviewed
  const [reviewForm,      setReviewForm]      = useState({ title: '', category: 'Client upload', makeVisible: false });

  const refreshRequests = React.useCallback(async () => {
    if (!matterId) return;
    setReqLoading(true);
    const { data, error } = await listPortalDocumentRequestsForFirm(matterId);
    setReqLoading(false);
    if (error) { toast.error('Could not load requests: ' + error.message); return; }
    setRequests(Array.isArray(data) ? data : []);
  }, [matterId]);

  useEffect(() => { refreshRequests(); }, [refreshRequests]);

  const submitRequest = async () => {
    if (!matter?.client_id) { toast.error('This matter has no client to request from.'); return; }
    if (!reqForm.title.trim()) return;
    setSavingReq(true);
    const dueAt = reqForm.dueAt ? new Date(reqForm.dueAt).toISOString() : null;
    const { error } = await createPortalDocumentRequest(
      matterId,
      matter.client_id,
      reqForm.title.trim(),
      reqForm.description || null,
      dueAt,
    );
    setSavingReq(false);
    if (error) { toast.error('Could not create request: ' + error.message); return; }
    toast.success('Request sent to client.');
    setShowRequestForm(false);
    setReqForm({ title: '', description: '', dueAt: '' });
    refreshRequests();
  };

  const cancelRequest = async (r) => {
    if (!window.confirm(`Cancel request "${r.title}"?`)) return;
    const { error } = await cancelPortalDocumentRequest(r.id);
    if (error) { toast.error(error.message); return; }
    refreshRequests();
  };

  const startReview = (r) => {
    setReviewing(r);
    setReviewForm({
      title: r.title || r.upload_filename || '',
      category: 'Client upload',
      makeVisible: false,
    });
  };

  const submitAccept = async () => {
    if (!reviewing) return;
    const { error } = await acceptPortalDocumentRequest(reviewing.id, {
      title: reviewForm.title || null,
      category: reviewForm.category || 'Client upload',
      makeVisible: reviewForm.makeVisible,
    });
    if (error) { toast.error('Accept failed: ' + error.message); return; }
    toast.success('Document accepted and added to matter.');
    setReviewing(null);
    refreshRequests();
    // Refresh document list to pick up the new accepted document row.
    const { data: docs } = await listDocuments(firmId, { matterId });
    if (docs) setDocuments(docs);
  };

  const submitReject = async () => {
    if (!reviewing) return;
    const note = window.prompt('Reason for rejection (optional):', '');
    const { error } = await rejectPortalDocumentRequest(reviewing.id, note || null);
    if (error) { toast.error(error.message); return; }
    toast.success('Upload rejected. Client can re-upload.');
    setReviewing(null);
    refreshRequests();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    // Pre-fill the rename field with the source filename so the
    // common "I'm fine with this name" case is a one-click upload.
    // Users who want a different display name (e.g. "Retainer
    // Agreement — Kingsley Adae" instead of "Dockerfile") just edit.
    setUploadForm((f) => ({ ...f, name: file.name }));
    setShowUpload(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const { path, error: upErr } = await uploadDocumentFile(firmId, selectedFile);
    if (upErr) { setUploading(false); toast.error('Upload failed: ' + upErr.message); return; }
    // Use the (possibly user-edited) display name; fall back to the
    // source filename if the user cleared the field.
    const displayName = (uploadForm.name || '').trim() || selectedFile.name;
    const { data, error } = await createDocument(firmId, {
      matter_id:   matterId,
      name:        displayName,
      description: uploadForm.description || null,
      category:    uploadForm.category,
      file_path:   path,
      file_size:   selectedFile.size,
      mime_type:   selectedFile.type || null,
    });
    setUploading(false);
    if (error) {
      // Map storage-limit error to a clearer message + clean up the
      // orphan blob in Supabase Storage. Without the cleanup the file
      // sits in storage with no row pointing at it — burns customer
      // bandwidth and storage seconds until the reaper finds it.
      const mapped = mapStorageLimitError(error);
      if (mapped?.code === 'STORAGE_LIMIT') {
        toast.error(
          'Storage limit reached. Visit Settings → Team to buy more storage, ' +
          'or delete some existing documents to free space.',
          { duration: 10000 },
        );
      } else {
        toast.error('Could not save document record: ' + error.message);
      }
      // Best-effort orphan cleanup. If this fails the reaper will
      // eventually catch it; either way we don't bother the user
      // with a second error.
      try { await deleteDocumentFile(path); } catch (_) { /* swallow */ }
      return;
    }
    setDocuments((prev) => [data, ...prev]);
    setShowUpload(false);
    setSelectedFile(null);
    setUploadForm({ name: '', category: 'other', description: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openEdit = (doc) => {
    setEditForm({ name: doc.name || '', description: doc.description || '', category: doc.category || 'other' });
    setEditDoc(doc);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    setSavingEdit(true);
    const { data, error } = await updateDocument(editDoc.id, {
      name:        editForm.name.trim(),
      description: editForm.description || null,
      category:    editForm.category,
    });
    setSavingEdit(false);
    if (error) { toast.error(error.message); return; }
    setDocuments((prev) => prev.map((d) => d.id === editDoc.id ? { ...d, ...data } : d));
    setEditDoc(null);
  };

  const handleDownload = async (doc) => {
    const { data, error } = await getDocumentUrl(doc.file_path);
    if (error || !data?.signedUrl) { toast.error('Could not generate download link.'); return; }
    window.open(data.signedUrl, '_blank');
  };

  const handleDelete = async (doc) => {
    setDeletingDoc(doc);
  };

  const openRequests   = requests.filter((r) => r.status === 'open');
  const pendingReview  = requests.filter((r) => r.status === 'uploaded');
  const historyReqs    = requests.filter((r) => ['accepted','rejected','cancelled'].includes(r.status));

  return (
    <>
      {/* hidden file input */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />

      {/* ── Client document requests panel ───────────────────────── */}
      <Card className="mb-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Client document requests</p>
            <p className="text-xs text-slate-500">Ask the client for a specific document via the portal.</p>
          </div>
          <Button
            icon={Plus}
            size="sm"
            variant="ghost"
            onClick={() => setShowRequestForm(true)}
            disabled={!matter?.client_id}
            title={!matter?.client_id ? 'Set a client on this matter first' : ''}
          >
            Request document
          </Button>
        </div>
        <CardBody className="p-0">
          {pendingReview.length > 0 && (
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
              <p className="text-xs uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold mb-2">
                Awaiting your review ({pendingReview.length})
              </p>
              <ul className="space-y-2">
                {pendingReview.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.title}</p>
                      <p className="text-xs text-slate-500 truncate">
                        Uploaded: {r.upload_filename || '—'} {r.upload_size ? `· ${formatBytes(r.upload_size)}` : ''}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => startReview(r)}>Review</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {openRequests.length > 0 && (
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Open requests ({openRequests.length})
              </p>
              <ul className="space-y-2">
                {openRequests.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.title}</p>
                      <p className="text-xs text-slate-500">
                        Requested {formatDate(r.created_at)}
                        {r.due_at ? ` · due ${formatDate(r.due_at)}` : ''}
                      </p>
                    </div>
                    <button onClick={() => cancelRequest(r)} className="text-xs text-slate-500 hover:text-red-600">Cancel</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {historyReqs.length > 0 && (
            <details className="px-6 py-3">
              <summary className="text-xs uppercase tracking-wide text-slate-500 font-semibold cursor-pointer">
                History ({historyReqs.length})
              </summary>
              <ul className="mt-2 space-y-1">
                {historyReqs.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{r.title}</span>
                    <Badge tone={r.status === 'accepted' ? 'green' : r.status === 'rejected' ? 'red' : 'slate'}>
                      {r.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {!reqLoading && !requests.length && (
            <div className="px-6 py-4 text-sm text-slate-500">No requests yet.</div>
          )}
        </CardBody>
      </Card>

      <Card>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <p className="text-sm text-slate-500">{documents.length} document{documents.length !== 1 ? 's' : ''}</p>
          <Button icon={Plus} size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : 'Upload document'}
          </Button>
        </div>
        <CardBody className="p-0">
          {!documents.length ? (
            <EmptyState icon={FileText} title="No documents yet"
              description="Upload documents directly to this case file."
              action={<Button icon={Plus} onClick={() => fileInputRef.current?.click()}>Upload document</Button>} />
          ) : (
            <>
              {/* Mobile cards */}
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {documents.map((doc) => {
                  const Icon = fileIcon(doc.mime_type);
                  return (
                    <li key={doc.id} className="px-4 py-3 flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <Icon size={15} className="text-slate-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{doc.name}</p>
                          {doc.description && <p className="text-xs text-slate-400 truncate">{doc.description}</p>}
                          <p className="text-xs text-slate-500 capitalize">{doc.category?.replace('_', ' ') || '—'} · {formatBytes(doc.file_size)}</p>
                          <p className="text-xs text-slate-400">{formatDate(doc.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => openEdit(doc)} className="p-1.5 rounded text-slate-400 hover:text-indigo-600"><Pencil size={13} /></button>
                        <button onClick={() => handleDownload(doc)} className="p-1.5 rounded text-slate-400 hover:text-indigo-600"><Download size={13} /></button>
                        <button onClick={() => handleDelete(doc)} className="p-1.5 rounded text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2">Name</th>
                    <th className="text-left px-6 py-2">Category</th>
                    <th className="text-left px-6 py-2">Size</th>
                    <th className="text-left px-6 py-2">Uploaded</th>
                    <th className="px-6 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {documents.map((doc) => {
                    const Icon = fileIcon(doc.mime_type);
                    return (
                      <tr key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 group">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <Icon size={15} className="text-slate-400 flex-shrink-0" />
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {doc.name}
                                {doc.uploaded_via_portal && (
                                  <Badge tone="indigo">Client upload</Badge>
                                )}
                              </p>
                              {doc.description && <p className="text-xs text-slate-400">{doc.description}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3 capitalize text-slate-500">{doc.category?.replace('_', ' ') || '—'}</td>
                        <td className="px-6 py-3 text-slate-500">{formatBytes(doc.file_size)}</td>
                        <td className="px-6 py-3 text-slate-500">{formatDate(doc.created_at)}</td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openEdit(doc)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600" title="Edit"><Pencil size={14} /></button>
                            <button onClick={() => handleDownload(doc)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600" title="Download"><Download size={14} /></button>
                            <button onClick={() => handleDelete(doc)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Upload confirm modal */}
      <Modal
        open={showUpload && !!selectedFile}
        onClose={() => { setShowUpload(false); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
        title="Upload document"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowUpload(false); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <FileText size={20} className="text-indigo-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{selectedFile?.name}</p>
              <p className="text-xs text-slate-400">{selectedFile ? formatBytes(selectedFile.size) : ''}</p>
            </div>
          </div>
          {/* Rename field — pre-filled with the source filename so the
              common case is one-click upload, but the user can edit
              to give the document a meaningful name in the matter
              (e.g. "Retainer Agreement — signed copy" instead of
              the OS-generated "scan_001.pdf"). The underlying storage
              file keeps its original path; only the display name in
              the documents table changes. */}
          <div>
            <Label>Document name</Label>
            <Input
              value={uploadForm.name}
              onChange={(e) => setUploadForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={selectedFile?.name || 'Document name'}
            />
            <p className="text-xs text-slate-400 mt-1">
              Shown in the documents list. The original filename is kept on disk.
            </p>
          </div>
          <div>
            <Label>Category</Label>
            <Select options={DOC_CATEGORIES} value={uploadForm.category}
              onChange={(e) => setUploadForm((f) => ({ ...f, category: e.target.value }))} />
          </div>
          <div>
            <Label>Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea rows={2} value={uploadForm.description}
              onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Brief description of this document…" />
          </div>
        </div>
      </Modal>

      {/* Edit document modal */}
      <Modal
        open={!!editDoc}
        onClose={() => setEditDoc(null)}
        title="Edit document"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditDoc(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !editForm.name.trim()}>
              {savingEdit ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Document name *</Label>
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select
              options={DOC_CATEGORIES}
              value={editForm.category}
              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
            />
          </div>
          <div>
            <Label>Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              rows={2}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Brief description…"
            />
          </div>
        </div>
      </Modal>
      <ConfirmDeleteModal
        open={!!deletingDoc}
        onClose={() => setDeletingDoc(null)}
        onConfirm={async () => {
          if (!deletingDoc) return;
          await deleteDocumentFile(deletingDoc.file_path);
          await deleteDocument(deletingDoc.id);
          setDocuments((prev) => prev.filter((d) => d.id !== deletingDoc.id));
          setDeletingDoc(null);
        }}
        title="Delete document"
        description={deletingDoc ? `Delete "${deletingDoc.name}"? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />

      {/* New request modal */}
      <Modal
        open={showRequestForm}
        onClose={() => setShowRequestForm(false)}
        title="Request a document from the client"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowRequestForm(false)}>Cancel</Button>
            <Button onClick={submitRequest} disabled={savingReq || !reqForm.title.trim()}>
              {savingReq ? 'Sending…' : 'Send request'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Document title *</Label>
            <Input
              value={reqForm.title}
              onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })}
              placeholder="e.g. Signed engagement letter"
            />
          </div>
          <div>
            <Label>Notes for client <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              rows={3}
              value={reqForm.description}
              onChange={(e) => setReqForm({ ...reqForm, description: e.target.value })}
              placeholder="What you need and any context the client should know…"
            />
          </div>
          <div>
            <Label>Due date <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              type="date"
              value={reqForm.dueAt}
              onChange={(e) => setReqForm({ ...reqForm, dueAt: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* Review upload modal */}
      <Modal
        open={!!reviewing}
        onClose={() => setReviewing(null)}
        title={reviewing ? `Review: ${reviewing.title}` : 'Review upload'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReviewing(null)}>Close</Button>
            <Button variant="ghost" onClick={submitReject}>Reject</Button>
            <Button onClick={submitAccept}>Accept</Button>
          </>
        }
      >
        {reviewing && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <FileText size={20} className="text-indigo-500 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{reviewing.upload_filename}</p>
                <p className="text-xs text-slate-400">
                  {reviewing.upload_size ? formatBytes(reviewing.upload_size) : ''}
                  {reviewing.upload_mime ? ` · ${reviewing.upload_mime}` : ''}
                </p>
                <p className="text-xs text-slate-400">Uploaded {formatDate(reviewing.uploaded_at)}</p>
              </div>
            </div>
            <div>
              <Label>Save as document name</Label>
              <Input
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input
                value={reviewForm.category}
                onChange={(e) => setReviewForm({ ...reviewForm, category: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={reviewForm.makeVisible}
                onChange={(e) => setReviewForm({ ...reviewForm, makeVisible: e.target.checked })}
              />
              Also make this document visible to the client in their portal
            </label>
          </div>
        )}
      </Modal>
    </>
  );
};

// ─── Tasks Tab ───────────────────────────────────────────────────────────────
const TASK_STATUSES = [
  { value: 'pending',     label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed' },
  { value: 'blocked',     label: 'Blocked' },
];

const TASK_PRIORITIES = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const EMPTY_TASK = { title: '', description: '', due_date: '', status: 'pending', priority: 'medium', assigned_to: '' };

function taskStatusTone(status) {
  if (status === 'completed')   return 'green';
  if (status === 'in_progress') return 'blue';
  if (status === 'blocked')     return 'red';
  return 'amber';
}

function taskPriorityColor(priority) {
  if (priority === 'urgent') return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
  if (priority === 'high')   return 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400';
  if (priority === 'medium') return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400';
  return 'text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400';
}

const TasksTab = ({ tasks, setTasks, firmId, matterId }) => {
  const [showModal,    setShowModal]    = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [form,         setForm]         = useState(EMPTY_TASK);
  const [saving,       setSaving]       = useState(false);
  const [toggling,     setToggling]     = useState({});
  const [filter,       setFilter]       = useState('all'); // all | open | completed
  const [deletingTask, setDeletingTask] = useState(null);

  const openAdd = () => { setForm(EMPTY_TASK); setEditTarget(null); setShowModal(true); };
  const openEdit = (t) => {
    setForm({
      title:       t.title || '',
      description: t.description || '',
      due_date:    t.due_date || '',
      status:      t.status || 'pending',
      priority:    t.priority || 'medium',
      assigned_to: t.assigned_to || '',
    });
    setEditTarget(t);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = { ...form, due_date: form.due_date || null, assigned_to: form.assigned_to || null };
    if (editTarget) {
      const { data, error } = await updateTask(editTarget.id, payload);
      if (!error && data) setTasks((prev) => prev.map((t) => t.id === editTarget.id ? { ...t, ...data } : t));
    } else {
      const { data, error } = await createTask(firmId, { matter_id: matterId, ...payload });
      if (!error && data) setTasks((prev) => [data, ...prev]);
    }
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (t) => {
    setDeletingTask(t);
  };

  const toggleComplete = async (t) => {
    const cycle = { pending: 'in_progress', in_progress: 'completed', completed: 'pending' };
    const next = cycle[t.status] || 'in_progress';
    setToggling((g) => ({ ...g, [t.id]: true }));
    const { data, error } = await updateTask(t.id, { status: next });
    if (!error && data) setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, status: next } : x));
    setToggling((g) => ({ ...g, [t.id]: false }));
  };

  const openTasks       = tasks.filter((t) => t.status === 'pending');
  const inProgressTasks = tasks.filter((t) => t.status === 'in_progress');
  const completedTasks  = tasks.filter((t) => t.status === 'completed');

  const displayed = filter === 'open'
    ? openTasks
    : filter === 'in_progress'
    ? inProgressTasks
    : filter === 'completed'
    ? completedTasks
    : [...openTasks, ...inProgressTasks, ...completedTasks];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white">Tasks</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {openTasks.length} pending · {inProgressTasks.length} in progress · {completedTasks.length} completed
          </p>
        </div>
        <Button icon={Plus} onClick={openAdd}>Add task</Button>
      </div>

      {/* Filter pills */}
      {tasks.length > 0 && (
        <div className="flex gap-2">
          {[['all', 'All'], ['open', 'Pending'], ['in_progress', 'In Progress'], ['completed', 'Completed']].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1 rounded-full border transition ${
                filter === v
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:border-indigo-400 hover:text-indigo-600'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      {displayed.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={ClipboardList}
              title="No tasks yet"
              description="Track action items, research tasks, and follow-ups for this case."
              action={<Button icon={Plus} onClick={openAdd}>Add first task</Button>}
            />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-2">
          {displayed.map((t) => {
            const done = t.status === 'completed';
            const overdue = !done && t.due_date && new Date(t.due_date) < Date.now();
            return (
              <div
                key={t.id}
                className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 flex items-start gap-3 hover:shadow-sm transition"
              >
                {/* Status cycle button: pending → in_progress → completed → pending */}
                <button
                  onClick={() => toggleComplete(t)}
                  disabled={toggling[t.id]}
                  className="mt-0.5 flex-shrink-0 w-5 h-5 flex items-center justify-center transition-opacity hover:opacity-70"
                  title={
                    t.status === 'pending'     ? 'Move to In Progress' :
                    t.status === 'in_progress' ? 'Mark Complete' :
                                                 'Reset to Pending'
                  }
                >
                  {t.status === 'completed'   && <CheckCircle2 size={20} className="text-emerald-500" />}
                  {t.status === 'in_progress' && <Clock        size={20} className="text-blue-500" />}
                  {t.status === 'pending'     && <Circle       size={20} className="text-slate-400 dark:text-slate-600" />}
                </button>

                {/* Body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${done ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
                      {t.title}
                    </p>
                    <Badge tone={taskStatusTone(t.status)}>{t.status.replace('_', ' ')}</Badge>
                    {t.priority && t.priority !== 'medium' && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded capitalize ${taskPriorityColor(t.priority)}`}>
                        <Flag size={9} className="inline mr-0.5" />{t.priority}
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-0.5">
                    {t.due_date && (
                      <p className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                        {overdue ? '⚠ Overdue · ' : 'Due '}
                        {formatDate(t.due_date)}
                      </p>
                    )}
                    {t.assigned_to && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <User size={10} /> {t.assigned_to}
                      </p>
                    )}
                  </div>
                </div>

                {/* Hover actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => openEdit(t)}
                    className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-indigo-600"
                    title="Edit"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(t)}
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editTarget ? 'Edit task' : 'Add task'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.title.trim()}>
              {saving ? 'Saving…' : editTarget ? 'Save changes' : 'Add task'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Task title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Collect evidence from client"
            />
          </div>
          <div>
            <Label>Description <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Any additional details…"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} options={TASK_PRIORITIES} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={TASK_STATUSES} />
            </div>
            <div>
              <Label>Assigned to <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                placeholder="Name or role"
              />
            </div>
          </div>
        </div>
      </Modal>
      <ConfirmDeleteModal
        open={!!deletingTask}
        onClose={() => setDeletingTask(null)}
        onConfirm={async () => {
          if (!deletingTask) return;
          await deleteTask(deletingTask.id);
          setTasks((prev) => prev.filter((x) => x.id !== deletingTask.id));
          setDeletingTask(null);
        }}
        title="Delete task"
        description={deletingTask ? `Delete task "${deletingTask.title}"? This cannot be undone.` : ''}
        confirmWord="DELETE"
      />
    </div>
  );
};
